import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import QRCode from 'react-qr-code';
import { useAuth } from '../context/AuthContext';
import InventoryTab from '../components/InventoryTab';
import {
  getMenu, addMenuItem, updateMenuItem, deleteMenuItem,
  getParties, addParty, updateParty, deleteParty,
  getInvoices, addInvoice, updateInvoice, deleteInvoice,
  getPurchases, addPurchase,
  getExpenses, addExpense, deleteExpense,
  getOrders, updateOrderStatus,
  getInventory,
  updateMe, sendBillEmail,
} from '../api';

// ── Palette ──────────────────────────────────────────────────────────────────
const C = {
  bg:       '#0f1117',
  sidebar:  '#13161f',
  card:     '#1a1f2e',
  card2:    '#1e2535',
  border:   'rgba(255,255,255,0.07)',
  accent:   '#FF6B35',
  accent2:  '#ff8c5a',
  text:     '#f1f5f9',
  muted:    '#64748b',
  muted2:   '#94a3b8',
  green:    '#10b981',
  red:      '#ef4444',
  blue:     '#6366f1',
  yellow:   '#f59e0b',
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => '₹' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today = () => new Date().toISOString().split('T')[0];
const TAX_RATES = [0, 5, 12, 18, 28];
const UNITS = ['pcs', 'kg', 'g', 'L', 'ml', 'box', 'bag', 'dozen', 'pair', 'set', 'meter'];
const PAYMENT_MODES = ['Cash', 'UPI', 'Card', 'Credit', 'Bank Transfer', 'Cheque'];
const EXPENSE_CATS = ['Rent', 'Electricity', 'Staff Salary', 'Transport', 'Marketing', 'Maintenance', 'Supplies', 'Other'];

// ── Mobile context ────────────────────────────────────────────────────────────
const MobileCtx = React.createContext(false);
const useIsMobile = () => React.useContext(MobileCtx);

// ── Reusable UI pieces ────────────────────────────────────────────────────────
function Btn({ children, onClick, color = C.accent, small, disabled, outline, style = {} }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: small ? '6px 14px' : '9px 20px',
      background: outline ? 'transparent' : (disabled ? 'rgba(255,107,53,0.3)' : color),
      border: outline ? `1px solid ${color}` : 'none',
      borderRadius: 8, color: outline ? color : '#fff',
      fontWeight: 600, fontSize: small ? 12 : 13, cursor: disabled ? 'not-allowed' : 'pointer',
      whiteSpace: 'nowrap', ...style,
    }}>{children}</button>
  );
}

function Input({ label, value, onChange, type = 'text', placeholder, required, style = {}, small }) {
  return (
    <div style={{ marginBottom: small ? 8 : 14, ...style }}>
      {label && <label style={{ display: 'block', fontSize: 11, color: C.muted2, fontWeight: 600, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.5px' }}>{label}{required && ' *'}</label>}
      <input type={type} value={value} onChange={onChange} placeholder={placeholder}
        style={{ width: '100%', padding: small ? '8px 10px' : '10px 13px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, color: C.text, outline: 'none', boxSizing: 'border-box' }} />
    </div>
  );
}

function Select({ label, value, onChange, options, style = {} }) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      {label && <label style={{ display: 'block', fontSize: 11, color: C.muted2, fontWeight: 600, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.5px' }}>{label}</label>}
      <select value={value} onChange={onChange}
        style={{ width: '100%', padding: '10px 13px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, color: C.text, outline: 'none' }}>
        {options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
      </select>
    </div>
  );
}

function Badge({ label, color }) {
  const colors = { paid: C.green, unpaid: C.red, partial: C.yellow, credit: C.blue, cash: C.muted2, upi: C.blue, card: C.blue };
  const bg = colors[label?.toLowerCase()] || C.muted;
  return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, background: bg + '22', color: bg, fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>{label}</span>;
}

function Modal({ title, onClose, children, wide }) {
  const isMobile = useIsMobile();
  return (
    <div className={isMobile ? 'retail-modal-overlay' : ''}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'flex-start', justifyContent: 'center', padding: isMobile ? 0 : '24px 16px', overflowY: isMobile ? 'hidden' : 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={isMobile ? 'retail-modal-box' : ''}
        style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: isMobile ? '20px 20px 0 0' : 14, width: '100%', maxWidth: isMobile ? '100%' : (wide ? 860 : 520), boxShadow: '0 24px 64px rgba(0,0,0,0.6)', maxHeight: isMobile ? '92vh' : 'none', overflowY: isMobile ? 'auto' : 'visible' }}>
        {isMobile && <div style={{ width: 40, height: 4, background: 'rgba(255,255,255,.2)', borderRadius: 2, margin: '12px auto 0' }} />}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{title}</div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: 'none', color: C.muted2, borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        <div style={{ padding: '22px' }}>{children}</div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon, color, sub }) {
  return (
    <div style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px', flex: 1, minWidth: 140 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 22 }}>{icon}</span>
        <span style={{ fontSize: 12, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px' }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || C.text }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
const NAV = [
  { id: 'dashboard',  icon: '📊', label: 'Dashboard' },
  { id: 'orders',     icon: '🛎️',  label: 'Orders' },
  { id: 'sales',      icon: '📄', label: 'Sales Invoice' },
  { id: 'parties',    icon: '👥', label: 'Parties' },
  { id: 'items',      icon: '📦', label: 'Items' },
  { id: 'inventory',  icon: '🗄️',  label: 'Inventory' },
  { id: 'purchases',  icon: '🛒', label: 'Purchases' },
  { id: 'expenses',   icon: '💸', label: 'Expenses' },
  { id: 'reports',    icon: '📈', label: 'Reports' },
  { id: 'qr',         icon: '📱', label: 'QR & Share' },
  { id: 'settings',   icon: '⚙️',  label: 'Settings' },
  { id: 'guide',      icon: '🎓', label: 'How It Works' },
];

function Sidebar({ active, setActive, business, logout, pendingOrders }) {
  return (
    <div style={{ width: 220, minWidth: 220, background: C.sidebar, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', height: '100vh', position: 'sticky', top: 0 }}>
      {/* Logo */}
      <div style={{ padding: '20px 18px 16px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.accent }}>Scanningo</div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{business?.name}</div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '10px 10px', overflowY: 'auto' }}>
        {NAV.map(n => (
          <button key={n.id} onClick={() => setActive(n.id)} style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', border: 'none', borderRadius: 9, cursor: 'pointer', marginBottom: 2,
            background: active === n.id ? `${C.accent}20` : 'transparent',
            color: active === n.id ? C.accent : C.muted2,
            fontWeight: active === n.id ? 700 : 500, fontSize: 13, textAlign: 'left',
          }}>
            <span style={{ fontSize: 16 }}>{n.icon}</span>
            <span style={{ flex: 1 }}>{n.label}</span>
            {n.id === 'orders' && pendingOrders > 0 && (
              <span style={{ background: C.red, color: '#fff', borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 800 }}>{pendingOrders}</span>
            )}
            {active === n.id && n.id !== 'orders' && <span style={{ width: 3, height: 18, background: C.accent, borderRadius: 2 }} />}
          </button>
        ))}
      </nav>

      {/* Bottom */}
      <div style={{ padding: '12px 10px', borderTop: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, paddingLeft: 4 }}>ID: {business?.businessId}</div>
        <button onClick={logout} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 12px', border: 'none', borderRadius: 9, cursor: 'pointer', background: 'rgba(239,68,68,0.1)', color: '#f87171', fontSize: 13, fontWeight: 600 }}>
          🚪 Logout
        </button>
      </div>
    </div>
  );
}

// ── Mobile Top Bar ────────────────────────────────────────────────────────────
function MobileTopBar({ business, pendingOrders, isFullscreen, onToggleFS, isStandalone }) {
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 300, background: C.sidebar, borderBottom: `1px solid ${C.border}`, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', boxShadow: '0 2px 12px rgba(0,0,0,.4)' }}>
      <div>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.accent, lineHeight: 1.2 }}>Scanningo</div>
        <div style={{ fontSize: 10, color: C.muted, marginTop: 1, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{business?.name || ''}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {pendingOrders > 0 && (
          <span style={{ background: C.red, color: '#fff', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 800 }}>🛎️ {pendingOrders}</span>
        )}
        {!isStandalone && (
          isIOS ? (
            <button title="Tap Share → Add to Home Screen for app experience"
              style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted2, padding: '5px 10px', fontSize: 13, cursor: 'pointer' }}>⊕</button>
          ) : (
            <button onClick={onToggleFS} title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, color: isFullscreen ? C.accent : C.muted2, padding: '5px 10px', fontSize: 15, cursor: 'pointer' }}>
              {isFullscreen ? '⊠' : '⛶'}
            </button>
          )
        )}
      </div>
    </div>
  );
}

// ── Mobile Bottom Nav ─────────────────────────────────────────────────────────
const MOBILE_BOTTOM_NAV = [
  { id: 'dashboard', icon: '📊', label: 'Home' },
  { id: 'orders',    icon: '🛎️', label: 'Orders' },
  { id: 'sales',     icon: '📄', label: 'Sales' },
  { id: 'items',     icon: '📦', label: 'Items' },
  { id: '_more',     icon: '☰',  label: 'More' },
];

const MORE_NAV = [
  { id: 'parties',   icon: '👥', label: 'Parties' },
  { id: 'inventory', icon: '🗄️', label: 'Inventory' },
  { id: 'purchases', icon: '🛒', label: 'Purchases' },
  { id: 'expenses',  icon: '💸', label: 'Expenses' },
  { id: 'reports',   icon: '📈', label: 'Reports' },
  { id: 'qr',        icon: '📱', label: 'QR & Share' },
  { id: 'settings',  icon: '⚙️', label: 'Settings' },
  { id: 'guide',     icon: '🎓', label: 'How It Works' },
];

function MobileBottomNav({ active, setActive, pendingOrders, logout }) {
  const [showMore, setShowMore] = useState(false);

  const goTo = (id) => { setActive(id); setShowMore(false); };

  return (
    <>
      <div className="retail-bottom-nav" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 300, background: C.sidebar, borderTop: `1px solid ${C.border}`, display: 'flex', height: 64, boxShadow: '0 -4px 20px rgba(0,0,0,.4)' }}>
        {MOBILE_BOTTOM_NAV.map(n => {
          const isActive = n.id === '_more' ? showMore : (active === n.id && !showMore);
          const hasBadge = n.id === 'orders' && pendingOrders > 0;
          return (
            <button key={n.id} onClick={() => n.id === '_more' ? setShowMore(v => !v) : goTo(n.id)}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, border: 'none', background: 'transparent', cursor: 'pointer', position: 'relative', color: isActive ? C.accent : C.muted }}>
              <div style={{ position: 'relative' }}>
                <span style={{ fontSize: 20 }}>{n.icon}</span>
                {hasBadge && <span style={{ position: 'absolute', top: -4, right: -8, background: C.red, color: '#fff', borderRadius: 20, fontSize: 9, fontWeight: 800, padding: '1px 5px', minWidth: 16, textAlign: 'center' }}>{pendingOrders}</span>}
              </div>
              <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500 }}>{n.label}</span>
              {isActive && <div style={{ position: 'absolute', top: 0, left: '20%', right: '20%', height: 2, background: C.accent, borderRadius: '0 0 2px 2px' }} />}
            </button>
          );
        })}
      </div>

      {/* More sheet */}
      {showMore && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 290, background: 'rgba(0,0,0,0.6)' }}
          onClick={() => setShowMore(false)}>
          <div style={{ position: 'absolute', bottom: 64, left: 0, right: 0, background: C.card, borderRadius: '20px 20px 0 0', padding: '12px 0 8px', boxShadow: '0 -8px 40px rgba(0,0,0,.5)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ width: 40, height: 4, background: 'rgba(255,255,255,.15)', borderRadius: 2, margin: '0 auto 16px' }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 2, padding: '0 8px 12px' }}>
              {MORE_NAV.map(n => (
                <button key={n.id} onClick={() => goTo(n.id)}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '14px 8px', border: 'none', background: active === n.id ? `${C.accent}18` : 'transparent', borderRadius: 12, cursor: 'pointer', color: active === n.id ? C.accent : C.muted2 }}>
                  <span style={{ fontSize: 24 }}>{n.icon}</span>
                  <span style={{ fontSize: 11, fontWeight: 600 }}>{n.label}</span>
                </button>
              ))}
            </div>
            <div style={{ borderTop: `1px solid ${C.border}`, padding: '10px 16px' }}>
              <button onClick={() => { setShowMore(false); logout(); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px', border: 'none', borderRadius: 10, background: 'rgba(239,68,68,0.1)', color: '#f87171', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                🚪 Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Dashboard Tab ─────────────────────────────────────────────────────────────
function DashboardTab({ invoices, parties, items, expenses, setActive, onViewInvoice, pendingOrders }) {
  const isMobile = useIsMobile();
  const todayStr = today();
  const todaySales = invoices.filter(i => i.invoice_date === todayStr).reduce((s, i) => s + i.total, 0);
  const outstanding = invoices.filter(i => i.payment_status !== 'paid').reduce((s, i) => s + i.balance_due, 0);
  const todayExp = expenses.filter(e => e.date === todayStr).reduce((s, e) => s + e.amount, 0);
  const recent = invoices.slice(0, 8);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 800, color: C.text }}>Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening'} 👋</div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(5,1fr)', gap: isMobile ? 10 : 14, marginBottom: 20 }}>
        <KpiCard icon="💰" label="Today's Sales" value={fmt(todaySales)} color={C.green}
          sub={`${invoices.filter(i => i.invoice_date === todayStr).length} invoices`} />
        <KpiCard icon="⏳" label="Outstanding" value={fmt(outstanding)} color={C.red}
          sub={`${invoices.filter(i => i.payment_status !== 'paid').length} pending`} />
        <KpiCard icon="🛎️" label="New Orders" value={pendingOrders} color={pendingOrders > 0 ? C.red : C.muted2}
          sub="via QR / shop link" />
        <KpiCard icon="👥" label="Total Parties" value={parties.length} color={C.blue}
          sub={`${parties.filter(p => p.type === 'customer').length} customers`} />
        <KpiCard icon="💸" label="Today's Exp." value={fmt(todayExp)} color={C.yellow} />
      </div>

      {/* Quick actions */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'auto auto auto auto auto', gap: 8, marginBottom: 20 }}>
        <Btn onClick={() => setActive('sales')} style={{ background: `linear-gradient(135deg,${C.accent},#f7522b)`, padding: isMobile ? '12px 10px' : undefined }}>+ Create Invoice</Btn>
        <Btn onClick={() => setActive('orders')} color={pendingOrders > 0 ? C.red : C.blue} style={{ padding: isMobile ? '12px 10px' : undefined }}>
          {pendingOrders > 0 ? `🛎️ ${pendingOrders} New Order${pendingOrders > 1 ? 's' : ''}!` : '🛎️ View Orders'}
        </Btn>
        {!isMobile && <>
          <Btn onClick={() => setActive('parties')} color={C.blue}>+ Add Party</Btn>
          <Btn onClick={() => setActive('items')} color={C.muted}>+ Add Item</Btn>
          <Btn onClick={() => setActive('expenses')} color={C.yellow}>+ Add Expense</Btn>
        </>}
      </div>

      {/* Recent invoices */}
      <div style={{ background: C.card2, borderRadius: 12, border: `1px solid ${C.border}` }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Recent Invoices</div>
          <Btn small onClick={() => setActive('sales')} outline color={C.accent}>View All</Btn>
        </div>
        {recent.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: C.muted }}>No invoices yet. Create your first one!</div>
        ) : (
          <div>
            {recent.map(inv => (
              <div key={inv.id} onClick={() => onViewInvoice(inv)}
                style={{ display: 'flex', alignItems: 'center', padding: '12px 18px', borderBottom: `1px solid ${C.border}`, gap: 12, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,107,53,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.party_name || 'Walk-in'}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{inv.invoice_no} · {inv.invoice_date}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{fmt(inv.total)}</div>
                  <Badge label={inv.payment_status} />
                </div>
                <span style={{ fontSize: 11, color: C.muted }}>›</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sales Invoice Tab ─────────────────────────────────────────────────────────
function SalesTab({ invoices, setInvoices, parties, items, business, onViewInvoice }) {
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState('all');
  const [editPayment, setEditPayment] = useState(null);
  const [editInvoice, setEditInvoice] = useState(null);

  const filtered = invoices.filter(i => filter === 'all' || i.payment_status === filter);

  const totalFiltered = filtered.reduce((s, i) => s + i.total, 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>Sales Invoices</div>
          <div style={{ fontSize: 12, color: C.muted }}>{filtered.length} records · {fmt(totalFiltered)}</div>
        </div>
        <Btn onClick={() => setShowCreate(true)} style={{ background: `linear-gradient(135deg,${C.accent},#f7522b)` }}>+ Create Invoice</Btn>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['all','paid','unpaid','partial'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '6px 16px', borderRadius: 20, border: `1px solid ${filter === f ? C.accent : C.border}`,
            background: filter === f ? `${C.accent}20` : 'transparent', color: filter === f ? C.accent : C.muted2,
            fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
          }}>{f === 'all' ? 'All' : f}</button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: C.card2, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 110px 100px 100px 180px', gap: 0, background: '#111827', padding: '10px 18px' }}>
          {['Invoice #', 'Party', 'Date', 'Amount', 'Status', 'Actions'].map(h => (
            <div key={h} style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.5px' }}>{h}</div>
          ))}
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: C.muted }}>No invoices found.</div>
        ) : filtered.map(inv => (
          <div key={inv.id} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 110px 100px 100px 180px', gap: 0, padding: '12px 18px', borderBottom: `1px solid ${C.border}`, alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.accent, cursor: 'pointer' }} onClick={() => onViewInvoice(inv)}>{inv.invoice_no}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{inv.party_name || 'Walk-in'}</div>
              {inv.balance_due > 0 && <div style={{ fontSize: 11, color: C.red }}>Due: {fmt(inv.balance_due)}</div>}
            </div>
            <div style={{ fontSize: 12, color: C.muted }}>{inv.invoice_date}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{fmt(inv.total)}</div>
            <div><Badge label={inv.payment_status} /></div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              <Btn small color={C.blue} onClick={() => onViewInvoice(inv)}>View</Btn>
              <Btn small color={C.yellow} onClick={() => setEditInvoice(inv)}>Edit</Btn>
              {inv.payment_status !== 'paid' && (
                <Btn small color={C.green} onClick={() => setEditPayment(inv)}>Collect</Btn>
              )}
              <Btn small color={C.red} outline onClick={async () => {
                if (!window.confirm('Delete this invoice?')) return;
                await deleteInvoice(inv.id);
                setInvoices(prev => prev.filter(x => x.id !== inv.id));
              }}>✕</Btn>
            </div>
          </div>
        ))}
      </div>

      {showCreate && (
        <CreateInvoiceModal parties={parties} items={items} business={business}
          onClose={() => setShowCreate(false)}
          onSave={(inv) => { setInvoices(prev => [inv, ...prev]); setShowCreate(false); }} />
      )}

      {editPayment && (
        <CollectPaymentModal invoice={editPayment}
          onClose={() => setEditPayment(null)}
          onSave={(updated) => { setInvoices(prev => prev.map(i => i.id === updated.id ? updated : i)); setEditPayment(null); }} />
      )}

      {editInvoice && (
        <EditInvoiceModal invoice={editInvoice} items={items}
          onClose={() => setEditInvoice(null)}
          onSave={(updated) => { setInvoices(prev => prev.map(i => i.id === updated.id ? updated : i)); setEditInvoice(null); }} />
      )}
    </div>
  );
}

// ── Edit Invoice Modal (discount per item + overall) ──────────────────────────
function EditInvoiceModal({ invoice, items, onClose, onSave }) {
  const [rows, setRows] = useState(
    (invoice.items || []).map(r => ({ ...r, discount: parseFloat(r.discount || 0) }))
  );
  const [overallDisc, setOverallDisc] = useState(parseFloat(invoice.discount_amount || 0));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const updateRow = (i, field, val) =>
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));

  const rowBase   = (r) => parseFloat(r.price || 0) * parseFloat(r.qty || 0);
  const rowDisc   = (r) => rowBase(r) * (parseFloat(r.discount || 0) / 100);
  const rowTaxAmt = (r) => (rowBase(r) - rowDisc(r)) * (parseFloat(r.tax_rate || 0) / 100);
  const rowAmt    = (r) => rowBase(r) - rowDisc(r) + rowTaxAmt(r);

  const subtotal  = rows.reduce((s, r) => s + (rowBase(r) - rowDisc(r)), 0);
  const taxTotal  = rows.reduce((s, r) => s + rowTaxAmt(r), 0);
  const grandTotal = Math.max(0, subtotal + taxTotal - parseFloat(overallDisc || 0));

  const save = async () => {
    setSaving(true); setErr('');
    const updatedRows = rows.map(r => ({
      ...r,
      amount: parseFloat(rowAmt(r).toFixed(2)),
    }));
    try {
      const updated = await updateInvoice(invoice.id, {
        items: updatedRows,
        subtotal: parseFloat(subtotal.toFixed(2)),
        discount_amount: parseFloat(parseFloat(overallDisc || 0).toFixed(2)),
        tax_amount: parseFloat(taxTotal.toFixed(2)),
        total: parseFloat(grandTotal.toFixed(2)),
      });
      onSave(updated);
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  const totalItemDisc = rows.reduce((s, r) => s + rowDisc(r), 0);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px', overflowY: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: '100%', maxWidth: 800, boxShadow: '0 24px 64px rgba(0,0,0,.6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: `1px solid ${C.border}` }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: C.text }}>Edit Invoice — {invoice.invoice_no}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Add discounts per item or apply an overall discount</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,.06)', border: 'none', color: C.muted2, borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        <div style={{ padding: '20px 22px' }}>
          {/* Items table */}
          <div style={{ background: C.card2, borderRadius: 10, border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 60px 80px 100px 80px 90px', background: '#111827', padding: '9px 14px', gap: 8 }}>
              {['Item', 'Qty', 'Rate', 'Disc %', 'Tax%', 'Amount'].map(h => (
                <div key={h} style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase' }}>{h}</div>
              ))}
            </div>
            {rows.map((r, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 60px 80px 100px 80px 90px', padding: '10px 14px', borderBottom: `1px solid ${C.border}`, gap: 8, alignItems: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{r.name}</div>
                <div style={{ fontSize: 13, color: C.muted2 }}>{r.qty} {r.unit}</div>
                <div style={{ fontSize: 13, color: C.muted2 }}>{fmt(r.price)}</div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="number" min="0" max="100" step="0.5" value={r.discount}
                      onChange={e => updateRow(i, 'discount', parseFloat(e.target.value) || 0)}
                      style={{ width: 55, padding: '5px 7px', background: 'rgba(255,255,255,.05)', border: `1px solid ${r.discount > 0 ? C.yellow : C.border}`, borderRadius: 6, fontSize: 13, color: C.text, outline: 'none', textAlign: 'center' }} />
                    <span style={{ fontSize: 12, color: C.muted }}>%</span>
                  </div>
                  {r.discount > 0 && <div style={{ fontSize: 11, color: C.yellow, marginTop: 2 }}>- {fmt(rowDisc(r))}</div>}
                </div>
                <div style={{ fontSize: 12, color: C.muted }}>{r.tax_rate || 0}%</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: r.discount > 0 ? C.green : C.text }}>{fmt(rowAmt(r))}</div>
              </div>
            ))}
          </div>

          {/* Overall discount */}
          <div style={{ background: C.card2, borderRadius: 10, border: `1px solid ${C.border}`, padding: '16px 18px', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 12 }}>Overall Discount (flat ₹ off final bill)</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 13, color: C.muted }}>₹</span>
              <input type="number" min="0" step="1" value={overallDisc}
                onChange={e => setOverallDisc(parseFloat(e.target.value) || 0)}
                style={{ width: 120, padding: '9px 12px', background: 'rgba(255,255,255,.05)', border: `1px solid ${overallDisc > 0 ? C.accent : C.border}`, borderRadius: 8, fontSize: 15, color: C.text, outline: 'none' }} />
              {overallDisc > 0 && <span style={{ fontSize: 13, color: C.accent, fontWeight: 600 }}>- {fmt(overallDisc)} off total</span>}
            </div>
          </div>

          {/* Totals summary */}
          <div style={{ background: C.card2, borderRadius: 10, border: `1px solid ${C.border}`, padding: '14px 18px', marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div style={{ fontSize: 13, color: C.muted }}>Subtotal (before discount)</div>
              <div style={{ fontSize: 13, color: C.text, textAlign: 'right' }}>{fmt(rows.reduce((s, r) => s + rowBase(r), 0))}</div>
              {totalItemDisc > 0 && <>
                <div style={{ fontSize: 13, color: C.yellow }}>Item discounts</div>
                <div style={{ fontSize: 13, color: C.yellow, textAlign: 'right' }}>- {fmt(totalItemDisc)}</div>
              </>}
              {taxTotal > 0 && <>
                <div style={{ fontSize: 13, color: C.muted }}>Tax (GST)</div>
                <div style={{ fontSize: 13, color: C.text, textAlign: 'right' }}>{fmt(taxTotal)}</div>
              </>}
              {overallDisc > 0 && <>
                <div style={{ fontSize: 13, color: C.accent }}>Overall discount</div>
                <div style={{ fontSize: 13, color: C.accent, textAlign: 'right' }}>- {fmt(overallDisc)}</div>
              </>}
              <div style={{ fontSize: 16, fontWeight: 800, color: C.green, borderTop: `1px solid ${C.border}`, paddingTop: 8, marginTop: 4 }}>Final Total</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: C.green, textAlign: 'right', borderTop: `1px solid ${C.border}`, paddingTop: 8, marginTop: 4 }}>{fmt(grandTotal)}</div>
            </div>
            {grandTotal < parseFloat(invoice.total) && (
              <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>
                Original: {fmt(invoice.total)} → Saving: {fmt(parseFloat(invoice.total) - grandTotal)}
              </div>
            )}
          </div>

          {err && <div style={{ background: 'rgba(239,68,68,.1)', border: `1px solid ${C.red}44`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: C.red, marginBottom: 12 }}>{err}</div>}

          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={save} disabled={saving} style={{ flex: 1, background: `linear-gradient(135deg,${C.accent},#f7522b)` }}>
              {saving ? 'Saving…' : '✓ Save Changes & Update Bill'}
            </Btn>
            <Btn outline color={C.muted} onClick={onClose}>Cancel</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Create Invoice Modal ──────────────────────────────────────────────────────
function CreateInvoiceModal({ parties, items, business, onClose, onSave, initialParty }) {
  const customers = parties.filter(p => p.type === 'customer');
  const [partyId, setPartyId] = useState(initialParty?.id || '');
  const [partyName, setPartyName] = useState(initialParty?.name || '');
  const [partyPhone, setPartyPhone] = useState(initialParty?.phone || '');
  const [invDate, setInvDate] = useState(today());
  const [payMode, setPayMode] = useState('Cash');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState([{ item_id: '', name: '', qty: 1, unit: 'pcs', price: 0, discount: 0, tax_rate: 0 }]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [walkIn, setWalkIn] = useState(!initialParty);

  const selectParty = (p) => {
    setPartyId(p.id); setPartyName(p.name); setPartyPhone(p.phone);
  };

  const updateRow = (i, field, val) => {
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  };

  const addRow = () => setRows(prev => [...prev, { item_id: '', name: '', qty: 1, unit: 'pcs', price: 0, discount: 0, tax_rate: 0 }]);
  const removeRow = (i) => setRows(prev => prev.filter((_, idx) => idx !== i));

  const selectItem = (i, item) => {
    updateRow(i, 'item_id', item.id);
    updateRow(i, 'name', item.name);
    updateRow(i, 'price', item.price);
    updateRow(i, 'unit', item.unit || 'pcs');
    updateRow(i, 'tax_rate', item.tax_rate || 0);
  };

  const rowAmount = (r) => {
    const base = parseFloat(r.price || 0) * parseFloat(r.qty || 0);
    const afterDisc = base * (1 - parseFloat(r.discount || 0) / 100);
    return afterDisc;
  };
  const rowTax = (r) => rowAmount(r) * parseFloat(r.tax_rate || 0) / 100;
  const subtotal = rows.reduce((s, r) => s + rowAmount(r), 0);
  const taxTotal = rows.reduce((s, r) => s + rowTax(r), 0);
  const grandTotal = subtotal + taxTotal;
  const amountPaid = payMode.toLowerCase() === 'credit' ? 0 : grandTotal;

  const save = async () => {
    if (!rows.some(r => r.name)) { setErr('Add at least one item.'); return; }
    setSaving(true); setErr('');
    try {
      const invItems = rows.filter(r => r.name).map(r => ({
        item_id: r.item_id, name: r.name, qty: parseFloat(r.qty), unit: r.unit,
        price: parseFloat(r.price), discount: parseFloat(r.discount || 0),
        tax_rate: parseFloat(r.tax_rate || 0),
        amount: +(rowAmount(r) + rowTax(r)).toFixed(2),
      }));
      const inv = await addInvoice({
        invoice_date: invDate,
        party_id: partyId, party_name: walkIn ? 'Walk-in' : partyName, party_phone: partyPhone,
        items: invItems,
        subtotal: +subtotal.toFixed(2),
        discount_amount: 0,
        tax_amount: +taxTotal.toFixed(2),
        total: +grandTotal.toFixed(2),
        payment_mode: payMode.toLowerCase(),
        amount_paid: +amountPaid.toFixed(2),
        notes,
      });
      onSave(inv);
    } catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <Modal title="Create Sales Invoice" onClose={onClose} wide>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Party */}
        <div>
          <label style={{ fontSize: 11, color: C.muted2, fontWeight: 600, marginBottom: 5, display: 'block', textTransform: 'uppercase', letterSpacing: '.5px' }}>Party / Customer</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer', fontSize: 13, color: C.muted2 }}>
            <input type="checkbox" checked={walkIn} onChange={e => setWalkIn(e.target.checked)} style={{ accentColor: C.accent }} />
            Walk-in customer (no party)
          </label>
          {!walkIn && (
            <select value={partyId} onChange={e => { const p = customers.find(x => x.id === +e.target.value); if (p) selectParty(p); else setPartyId(''); }}
              style={{ width: '100%', padding: '10px 13px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, color: C.text, outline: 'none' }}>
              <option value="">Select customer…</option>
              {customers.map(p => <option key={p.id} value={p.id}>{p.name} {p.phone ? `(${p.phone})` : ''}</option>)}
            </select>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Input label="Date" type="date" value={invDate} onChange={e => setInvDate(e.target.value)} />
          <Select label="Payment Mode" value={payMode} onChange={e => setPayMode(e.target.value)} options={PAYMENT_MODES} />
        </div>
      </div>

      {/* Items Table */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.5px' }}>Items</div>
        <div style={{ background: '#111827', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 60px 70px 80px 70px 70px 80px 30px', gap: 0, padding: '8px 10px' }}>
            {['Item', 'Qty', 'Unit', 'Rate', 'Disc%', 'Tax%', 'Amount', ''].map(h => (
              <div key={h} style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: 'uppercase' }}>{h}</div>
            ))}
          </div>
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 60px 70px 80px 70px 70px 80px 30px', gap: 4, padding: '6px 10px', borderTop: `1px solid ${C.border}`, alignItems: 'center' }}>
              {/* Item name with dropdown */}
              <div style={{ position: 'relative' }}>
                <input value={r.name} onChange={e => updateRow(i, 'name', e.target.value)} placeholder="Item name"
                  list={`items-${i}`} style={{ width: '100%', padding: '6px 8px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 12, color: C.text, outline: 'none' }} />
                <datalist id={`items-${i}`}>
                  {items.filter(it => it.active !== false).map(it => <option key={it.id} value={it.name} onClick={() => selectItem(i, it)} />)}
                </datalist>
              </div>
              <input type="number" value={r.qty} onChange={e => updateRow(i, 'qty', e.target.value)} min="0"
                style={{ padding: '6px 6px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 12, color: C.text, outline: 'none', width: '100%' }} />
              <select value={r.unit} onChange={e => updateRow(i, 'unit', e.target.value)}
                style={{ padding: '6px 4px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 11, color: C.text, outline: 'none', width: '100%' }}>
                {UNITS.map(u => <option key={u}>{u}</option>)}
              </select>
              <input type="number" value={r.price} onChange={e => updateRow(i, 'price', e.target.value)} min="0"
                style={{ padding: '6px 6px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 12, color: C.text, outline: 'none', width: '100%' }} />
              <input type="number" value={r.discount} onChange={e => updateRow(i, 'discount', e.target.value)} min="0" max="100"
                style={{ padding: '6px 6px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 12, color: C.text, outline: 'none', width: '100%' }} />
              <select value={r.tax_rate} onChange={e => updateRow(i, 'tax_rate', e.target.value)}
                style={{ padding: '6px 4px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 11, color: C.text, outline: 'none', width: '100%' }}>
                {TAX_RATES.map(t => <option key={t} value={t}>{t}%</option>)}
              </select>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, textAlign: 'right' }}>
                {fmt(rowAmount(r) + rowTax(r))}
              </div>
              <button onClick={() => removeRow(i)} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
            </div>
          ))}
        </div>
        <button onClick={addRow} style={{ marginTop: 8, background: 'none', border: `1px dashed ${C.border}`, borderRadius: 7, padding: '7px 16px', color: C.muted2, fontSize: 12, cursor: 'pointer', width: '100%' }}>
          + Add Row
        </button>
      </div>

      {/* Totals + Notes */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <Input label="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes for this invoice" />
        </div>
        <div style={{ background: '#111827', borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13, color: C.muted }}>
            <span>Subtotal</span><span>{fmt(subtotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13, color: C.muted }}>
            <span>GST / Tax</span><span>{fmt(taxTotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}`, fontSize: 16, fontWeight: 800, color: C.accent }}>
            <span>Total</span><span>{fmt(grandTotal)}</span>
          </div>
          {payMode.toLowerCase() === 'credit' && (
            <div style={{ marginTop: 6, fontSize: 12, color: C.red, fontWeight: 600 }}>⚠️ Credit — full amount due</div>
          )}
        </div>
      </div>

      {err && <div style={{ background: 'rgba(239,68,68,0.1)', border: `1px solid rgba(239,68,68,0.3)`, borderRadius: 8, padding: '9px 14px', fontSize: 13, color: '#f87171', marginTop: 12 }}>{err}</div>}

      <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
        <Btn outline color={C.muted2} onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={saving} style={{ background: `linear-gradient(135deg,${C.accent},#f7522b)` }}>
          {saving ? 'Saving…' : '💾 Save Invoice'}
        </Btn>
      </div>
    </Modal>
  );
}

function CollectPaymentModal({ invoice, onClose, onSave }) {
  const [amount, setAmount] = useState(invoice.balance_due);
  const [mode, setMode] = useState('Cash');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const newPaid = parseFloat(invoice.amount_paid) + parseFloat(amount);
      const updated = await updateInvoice(invoice.id, { amount_paid: +newPaid.toFixed(2) });
      onSave(updated);
    } catch (e) { alert(e.message); setSaving(false); }
  };

  return (
    <Modal title={`Collect Payment — ${invoice.invoice_no}`} onClose={onClose}>
      <div style={{ marginBottom: 12, padding: '12px 16px', background: '#111827', borderRadius: 8 }}>
        <div style={{ fontSize: 13, color: C.muted }}>Total: <b style={{ color: C.text }}>{fmt(invoice.total)}</b> · Paid: <b style={{ color: C.green }}>{fmt(invoice.amount_paid)}</b> · <span style={{ color: C.red }}>Due: {fmt(invoice.balance_due)}</span></div>
      </div>
      <Input label="Amount Received" type="number" value={amount} onChange={e => setAmount(e.target.value)} />
      <Select label="Payment Mode" value={mode} onChange={e => setMode(e.target.value)} options={PAYMENT_MODES.filter(m => m !== 'Credit')} />
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <Btn outline color={C.muted2} onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={saving} color={C.green}>{saving ? 'Saving…' : '✓ Collect'}</Btn>
      </div>
    </Modal>
  );
}

// ── Parties Tab ───────────────────────────────────────────────────────────────
function PartiesTab({ parties, setParties }) {
  const [showAdd, setShowAdd] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [editP, setEditP] = useState(null);

  const list = parties.filter(p => typeFilter === 'all' || p.type === typeFilter);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>Parties</div>
        <Btn onClick={() => setShowAdd(true)} style={{ background: `linear-gradient(135deg,${C.accent},#f7522b)` }}>+ Add Party</Btn>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['all','All'], ['customer','Customers'], ['supplier','Suppliers']].map(([v, l]) => (
          <button key={v} onClick={() => setTypeFilter(v)} style={{
            padding: '6px 16px', borderRadius: 20, border: `1px solid ${typeFilter === v ? C.accent : C.border}`,
            background: typeFilter === v ? `${C.accent}20` : 'transparent', color: typeFilter === v ? C.accent : C.muted2,
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>{l}</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {list.map(p => (
          <div key={p.id} style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{p.name}</div>
                {p.phone && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>📞 {p.phone}</div>}
                {p.gstin && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>GST: {p.gstin}</div>}
              </div>
              <Badge label={p.type} color={p.type === 'customer' ? C.blue : C.yellow} />
            </div>
            {p.balance !== 0 && (
              <div style={{ marginTop: 10, padding: '8px 12px', background: p.balance > 0 ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', borderRadius: 8, fontSize: 13, fontWeight: 700, color: p.balance > 0 ? C.red : C.green }}>
                {p.balance > 0 ? `Outstanding: ${fmt(p.balance)}` : `Advance: ${fmt(Math.abs(p.balance))}`}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <Btn small outline color={C.accent} onClick={() => setEditP(p)}>Edit</Btn>
              <Btn small outline color={C.red} onClick={async () => {
                if (!window.confirm(`Delete ${p.name}?`)) return;
                await deleteParty(p.id);
                setParties(prev => prev.filter(x => x.id !== p.id));
              }}>Delete</Btn>
            </div>
          </div>
        ))}
        {list.length === 0 && <div style={{ color: C.muted, fontSize: 14 }}>No parties found.</div>}
      </div>

      {(showAdd || editP) && (
        <PartyFormModal party={editP} onClose={() => { setShowAdd(false); setEditP(null); }}
          onSave={async (data) => {
            if (editP) {
              const updated = await updateParty(editP.id, data);
              setParties(prev => prev.map(p => p.id === updated.id ? updated : p));
            } else {
              const created = await addParty(data);
              setParties(prev => [...prev, created]);
            }
            setShowAdd(false); setEditP(null);
          }} />
      )}
    </div>
  );
}

function PartyFormModal({ party, onClose, onSave }) {
  const [form, setForm] = useState({ type: 'customer', name: '', phone: '', email: '', address: '', gstin: '', opening_balance: 0, ...party });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try { await onSave(form); } catch (e) { alert(e.message); setSaving(false); }
  };

  return (
    <Modal title={party ? `Edit ${party.name}` : 'Add Party'} onClose={onClose}>
      <Select label="Type" value={form.type} onChange={e => set('type', e.target.value)} options={[{value:'customer',label:'Customer'},{value:'supplier',label:'Supplier'}]} />
      <Input label="Name" value={form.name} onChange={e => set('name', e.target.value)} required />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Input label="Phone" value={form.phone} onChange={e => set('phone', e.target.value)} />
        <Input label="Email" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
      </div>
      <Input label="Address" value={form.address} onChange={e => set('address', e.target.value)} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Input label="GSTIN" value={form.gstin} onChange={e => set('gstin', e.target.value)} />
        <Input label="Opening Balance (₹)" type="number" value={form.opening_balance} onChange={e => set('opening_balance', e.target.value)} />
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <Btn outline color={C.muted2} onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : party ? 'Update' : 'Add Party'}</Btn>
      </div>
    </Modal>
  );
}

// ── Items Tab ─────────────────────────────────────────────────────────────────
function ItemsTab({ items, setItems, inventory }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [search, setSearch] = useState('');

  const list = items.filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase()));

  // Map inventory by name for quick stock lookup
  const stockMap = {};
  (inventory || []).forEach(inv => { stockMap[inv.name.toLowerCase()] = inv; });

  const stockOf = (item) => {
    const inv = stockMap[item.name.toLowerCase()];
    if (!inv) return null;
    return inv;
  };

  const stockBadge = (item) => {
    const inv = stockOf(item);
    if (!inv) return <span style={{ fontSize: 11, color: C.muted }}>—</span>;
    const qty = inv.quantity;
    const low = inv.min_stock > 0 && qty <= inv.min_stock;
    const out = qty <= 0;
    const color = out ? C.red : low ? C.yellow : C.green;
    return <span style={{ fontSize: 12, fontWeight: 700, color }}>{out ? 'Out' : `${qty} ${inv.unit || ''}`}</span>;
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>Items / Products</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Stock levels synced from Inventory tab</div>
        </div>
        <Btn onClick={() => setShowAdd(true)} style={{ background: `linear-gradient(135deg,${C.accent},#f7522b)` }}>+ Add Item</Btn>
      </div>

      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search items…"
        style={{ width: '100%', maxWidth: 320, padding: '10px 14px', background: C.card2, border: `1px solid ${C.border}`, borderRadius: 9, fontSize: 13, color: C.text, outline: 'none', marginBottom: 16, boxSizing: 'border-box' }} />

      <div style={{ background: C.card2, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 80px 70px 80px 80px 60px 70px 110px', padding: '10px 18px', background: '#111827' }}>
          {['Item Name', 'Category', 'Unit', 'Purchase ₹', 'Sale ₹', 'Tax%', 'Stock', 'Actions'].map(h => (
            <div key={h} style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.5px' }}>{h}</div>
          ))}
        </div>
        {list.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: C.muted }}>No items found.</div>
        ) : list.map(item => (
          <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 70px 80px 80px 60px 70px 110px', padding: '11px 18px', borderBottom: `1px solid ${C.border}`, alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{item.name}</div>
              {item.description && <div style={{ fontSize: 11, color: C.muted }}>{item.description}</div>}
            </div>
            <div style={{ fontSize: 12, color: C.muted }}>{item.category}</div>
            <div style={{ fontSize: 12, color: C.muted }}>{item.unit || 'pcs'}</div>
            <div style={{ fontSize: 13, color: C.muted }}>{item.purchase_price ? fmt(item.purchase_price) : '—'}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.accent }}>{fmt(item.price)}</div>
            <div style={{ fontSize: 12, color: C.muted }}>{item.tax_rate || 0}%</div>
            <div>{stockBadge(item)}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn small outline color={C.accent} onClick={() => setEditItem(item)}>Edit</Btn>
              <Btn small outline color={C.red} onClick={async () => {
                if (!window.confirm(`Delete ${item.name}?`)) return;
                await deleteMenuItem(item.id);
                setItems(prev => prev.filter(x => x.id !== item.id));
              }}>✕</Btn>
            </div>
          </div>
        ))}
      </div>

      {(showAdd || editItem) && (
        <ItemFormModal item={editItem} onClose={() => { setShowAdd(false); setEditItem(null); }}
          onSave={async (data) => {
            if (editItem) {
              const updated = await updateMenuItem(editItem.id, data);
              setItems(prev => prev.map(x => x.id === updated.id ? updated : x));
            } else {
              const created = await addMenuItem(data);
              setItems(prev => [...prev, created]);
            }
            setShowAdd(false); setEditItem(null);
          }} />
      )}
    </div>
  );
}

function ItemFormModal({ item, onClose, onSave }) {
  const [form, setForm] = useState({
    name: '', category: 'General', unit: 'pcs', price: '', purchase_price: '',
    description: '', active: true,
    ...item,
    tax_rate: item?.tax_rate ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { alert('Name is required'); return; }
    if (!form.price) { alert('Sale price is required'); return; }
    setSaving(true);
    try { await onSave(form); } catch (e) { alert(e.message); setSaving(false); }
  };

  return (
    <Modal title={item ? `Edit ${item.name}` : 'Add Item'} onClose={onClose}>
      <Input label="Item / Product Name" value={form.name} onChange={e => set('name', e.target.value)} required />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Input label="Category" value={form.category} onChange={e => set('category', e.target.value)} placeholder="e.g. Grocery, FMCG" />
        <Select label="Unit" value={form.unit} onChange={e => set('unit', e.target.value)} options={UNITS} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Input label="Sale Price (₹)" type="number" value={form.price} onChange={e => set('price', e.target.value)} required />
        <Input label="Purchase Price (₹)" type="number" value={form.purchase_price} onChange={e => set('purchase_price', e.target.value)} />
        <Select label="GST / Tax %" value={form.tax_rate} onChange={e => set('tax_rate', +e.target.value)} options={TAX_RATES.map(t => ({ value: t, label: `${t}%` }))} />
      </div>
      <Input label="Description (optional)" value={form.description} onChange={e => set('description', e.target.value)} />
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <Btn outline color={C.muted2} onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : item ? 'Update Item' : 'Add Item'}</Btn>
      </div>
    </Modal>
  );
}

// ── Purchases Tab ─────────────────────────────────────────────────────────────
function PurchasesTab({ purchases, setPurchases, parties, items }) {
  const [showAdd, setShowAdd] = useState(false);
  const suppliers = parties.filter(p => p.type === 'supplier');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>Purchases</div>
        <Btn onClick={() => setShowAdd(true)} style={{ background: `linear-gradient(135deg,${C.accent},#f7522b)` }}>+ Record Purchase</Btn>
      </div>
      <div style={{ background: C.card2, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr 120px 120px 100px', padding: '10px 18px', background: '#111827' }}>
          {['Ref #', 'Supplier', 'Date', 'Amount', 'Mode'].map(h => (
            <div key={h} style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase' }}>{h}</div>
          ))}
        </div>
        {purchases.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: C.muted }}>No purchases recorded yet.</div>
        ) : purchases.map(p => (
          <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 120px 120px 100px', padding: '12px 18px', borderBottom: `1px solid ${C.border}`, alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.accent }}>{p.ref_no}</div>
            <div style={{ fontSize: 13, color: C.text }}>{p.party_name || '—'}</div>
            <div style={{ fontSize: 12, color: C.muted }}>{p.date}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{fmt(p.total)}</div>
            <div><Badge label={p.payment_mode} /></div>
          </div>
        ))}
      </div>
      {showAdd && (
        <PurchaseFormModal suppliers={suppliers} items={items} onClose={() => setShowAdd(false)}
          onSave={async (data) => {
            const created = await addPurchase(data);
            setPurchases(prev => [created, ...prev]);
            setShowAdd(false);
          }} />
      )}
    </div>
  );
}

function PurchaseFormModal({ suppliers, items, onClose, onSave }) {
  const [partyId, setPartyId] = useState('');
  const [partyName, setPartyName] = useState('');
  const [date, setDate] = useState(today());
  const [payMode, setPayMode] = useState('Cash');
  const [rows, setRows] = useState([{ name: '', qty: 1, unit: 'pcs', price: 0, tax_rate: 0 }]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const updateRow = (i, k, v) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const rowAmt = (r) => parseFloat(r.qty || 0) * parseFloat(r.price || 0) * (1 + parseFloat(r.tax_rate || 0) / 100);
  const total = rows.reduce((s, r) => s + rowAmt(r), 0);

  const save = async () => {
    if (!rows.some(r => r.name)) { alert('Add at least one item'); return; }
    setSaving(true);
    try {
      await onSave({
        party_id: partyId, party_name: partyName, date, payment_mode: payMode.toLowerCase(), notes,
        items: rows.filter(r => r.name).map(r => ({ name: r.name, qty: +r.qty, unit: r.unit, price: +r.price, tax_rate: +r.tax_rate, amount: +rowAmt(r).toFixed(2) })),
        subtotal: +rows.reduce((s, r) => s + +r.qty * +r.price, 0).toFixed(2),
        tax_amount: +rows.reduce((s, r) => s + +r.qty * +r.price * +r.tax_rate / 100, 0).toFixed(2),
        total: +total.toFixed(2),
      });
    } catch (e) { alert(e.message); setSaving(false); }
  };

  return (
    <Modal title="Record Purchase" onClose={onClose} wide>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div>
          <label style={{ fontSize: 11, color: C.muted2, fontWeight: 600, display: 'block', marginBottom: 5, textTransform: 'uppercase' }}>Supplier (optional)</label>
          <select value={partyId} onChange={e => { const s = suppliers.find(x => x.id === +e.target.value); setPartyId(e.target.value); setPartyName(s?.name || ''); }}
            style={{ width: '100%', padding: '10px 13px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, color: C.text, outline: 'none' }}>
            <option value="">Select supplier…</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <Input label="Date" type="date" value={date} onChange={e => setDate(e.target.value)} />
        <Select label="Payment Mode" value={payMode} onChange={e => setPayMode(e.target.value)} options={PAYMENT_MODES} />
      </div>
      {/* Items rows */}
      <div style={{ background: '#111827', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 60px 70px 80px 70px 80px 30px', padding: '8px 10px' }}>
          {['Item', 'Qty', 'Unit', 'Rate', 'Tax%', 'Amount', ''].map(h => (
            <div key={h} style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: 'uppercase' }}>{h}</div>
          ))}
        </div>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 60px 70px 80px 70px 80px 30px', gap: 4, padding: '6px 10px', borderTop: `1px solid ${C.border}`, alignItems: 'center' }}>
            <input value={r.name} onChange={e => updateRow(i, 'name', e.target.value)} list={`pitems-${i}`} placeholder="Item"
              style={{ padding: '6px 8px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 12, color: C.text, outline: 'none', width: '100%' }} />
            <datalist id={`pitems-${i}`}>{items.map(it => <option key={it.id} value={it.name} />)}</datalist>
            <input type="number" value={r.qty} onChange={e => updateRow(i, 'qty', e.target.value)} style={{ padding: '6px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 12, color: C.text, outline: 'none', width: '100%' }} />
            <select value={r.unit} onChange={e => updateRow(i, 'unit', e.target.value)} style={{ padding: '6px 4px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 11, color: C.text, outline: 'none', width: '100%' }}>
              {UNITS.map(u => <option key={u}>{u}</option>)}
            </select>
            <input type="number" value={r.price} onChange={e => updateRow(i, 'price', e.target.value)} style={{ padding: '6px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 12, color: C.text, outline: 'none', width: '100%' }} />
            <select value={r.tax_rate} onChange={e => updateRow(i, 'tax_rate', e.target.value)} style={{ padding: '6px 4px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 5, fontSize: 11, color: C.text, outline: 'none', width: '100%' }}>
              {TAX_RATES.map(t => <option key={t} value={t}>{t}%</option>)}
            </select>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.accent }}>{fmt(rowAmt(r))}</div>
            <button onClick={() => setRows(prev => prev.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
          </div>
        ))}
      </div>
      <button onClick={() => setRows(prev => [...prev, { name: '', qty: 1, unit: 'pcs', price: 0, tax_rate: 0 }])}
        style={{ background: 'none', border: `1px dashed ${C.border}`, borderRadius: 7, padding: '7px 16px', color: C.muted2, fontSize: 12, cursor: 'pointer', width: '100%', marginBottom: 12 }}>+ Add Row</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Input label="Notes" value={notes} onChange={e => setNotes(e.target.value)} style={{ flex: 1, marginRight: 16, marginBottom: 0 }} />
        <div style={{ fontSize: 18, fontWeight: 800, color: C.accent }}>Total: {fmt(total)}</div>
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
        <Btn outline color={C.muted2} onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : '💾 Save Purchase'}</Btn>
      </div>
    </Modal>
  );
}

// ── Expenses Tab ──────────────────────────────────────────────────────────────
function ExpensesTab({ expenses, setExpenses }) {
  const [showAdd, setShowAdd] = useState(false);
  const thisMonth = new Date().toISOString().slice(0, 7);
  const monthTotal = expenses.filter(e => e.date?.startsWith(thisMonth)).reduce((s, e) => s + e.amount, 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>Expenses</div>
          <div style={{ fontSize: 12, color: C.muted }}>This month: <b style={{ color: C.red }}>{fmt(monthTotal)}</b></div>
        </div>
        <Btn onClick={() => setShowAdd(true)} style={{ background: `linear-gradient(135deg,${C.accent},#f7522b)` }}>+ Add Expense</Btn>
      </div>
      <div style={{ background: C.card2, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 120px 100px 100px 80px', padding: '10px 18px', background: '#111827' }}>
          {['Category', 'Description', 'Date', 'Amount', 'Mode', ''].map(h => (
            <div key={h} style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase' }}>{h}</div>
          ))}
        </div>
        {expenses.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: C.muted }}>No expenses recorded.</div>
        ) : expenses.map(e => (
          <div key={e.id} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 120px 100px 100px 80px', padding: '11px 18px', borderBottom: `1px solid ${C.border}`, alignItems: 'center' }}>
            <div><Badge label={e.category} /></div>
            <div style={{ fontSize: 13, color: C.text }}>{e.description || '—'}</div>
            <div style={{ fontSize: 12, color: C.muted }}>{e.date}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.red }}>{fmt(e.amount)}</div>
            <div><Badge label={e.payment_mode} /></div>
            <Btn small outline color={C.red} onClick={async () => {
              if (!window.confirm('Delete this expense?')) return;
              await deleteExpense({ id: e.id });
              setExpenses(prev => prev.filter(x => x.id !== e.id));
            }}>✕</Btn>
          </div>
        ))}
      </div>
      {showAdd && (
        <ExpenseFormModal onClose={() => setShowAdd(false)}
          onSave={async (data) => {
            const created = await addExpense(data);
            setExpenses(prev => [created, ...prev]);
            setShowAdd(false);
          }} />
      )}
    </div>
  );
}

function ExpenseFormModal({ onClose, onSave }) {
  const [form, setForm] = useState({ category: 'Other', description: '', amount: '', date: today(), payment_mode: 'Cash', notes: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.amount) { alert('Amount is required'); return; }
    setSaving(true);
    try { await onSave({ ...form, amount: parseFloat(form.amount), payment_mode: form.payment_mode.toLowerCase() }); }
    catch (e) { alert(e.message); setSaving(false); }
  };

  return (
    <Modal title="Add Expense" onClose={onClose}>
      <Select label="Category" value={form.category} onChange={e => set('category', e.target.value)} options={EXPENSE_CATS} />
      <Input label="Description" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief description" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Input label="Amount (₹)" type="number" value={form.amount} onChange={e => set('amount', e.target.value)} required />
        <Input label="Date" type="date" value={form.date} onChange={e => set('date', e.target.value)} />
        <Select label="Payment Mode" value={form.payment_mode} onChange={e => set('payment_mode', e.target.value)} options={PAYMENT_MODES.filter(m => m !== 'Credit')} />
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <Btn outline color={C.muted2} onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Add Expense'}</Btn>
      </div>
    </Modal>
  );
}

// ── Reports Tab ───────────────────────────────────────────────────────────────
function ReportsTab({ invoices, expenses, parties }) {
  const [period, setPeriod] = useState('month');

  const now = new Date();
  const filterDate = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (period === 'today') return dateStr === today();
    if (period === 'week') { const w = new Date(); w.setDate(w.getDate() - 7); return d >= w; }
    if (period === 'month') return dateStr.startsWith(now.toISOString().slice(0, 7));
    if (period === 'year') return dateStr.startsWith(now.getFullYear().toString());
    return true;
  };

  const filtInv = invoices.filter(i => filterDate(i.invoice_date));
  const filtExp = expenses.filter(e => filterDate(e.date));

  const totalSales = filtInv.reduce((s, i) => s + i.total, 0);
  const totalCollected = filtInv.reduce((s, i) => s + i.amount_paid, 0);
  const totalOutstanding = filtInv.reduce((s, i) => s + i.balance_due, 0);
  const totalExpenses = filtExp.reduce((s, e) => s + e.amount, 0);
  const netProfit = totalCollected - totalExpenses;

  // GST summary
  const gstCollected = filtInv.reduce((s, i) => s + i.tax_amount, 0);

  // Payment mode breakdown
  const modeBreakdown = filtInv.reduce((acc, i) => {
    acc[i.payment_mode] = (acc[i.payment_mode] || 0) + i.total;
    return acc;
  }, {});

  // Category expense breakdown
  const expBreakdown = filtExp.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + e.amount;
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>Reports</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {[['today','Today'],['week','This Week'],['month','This Month'],['year','This Year'],['all','All Time']].map(([v, l]) => (
            <button key={v} onClick={() => setPeriod(v)} style={{
              padding: '6px 14px', borderRadius: 20, border: `1px solid ${period === v ? C.accent : C.border}`,
              background: period === v ? `${C.accent}20` : 'transparent', color: period === v ? C.accent : C.muted2,
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>{l}</button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>
        <KpiCard icon="💰" label="Total Sales" value={fmt(totalSales)} color={C.accent} sub={`${filtInv.length} invoices`} />
        <KpiCard icon="✅" label="Collected" value={fmt(totalCollected)} color={C.green} />
        <KpiCard icon="⏳" label="Outstanding" value={fmt(totalOutstanding)} color={C.red} />
        <KpiCard icon="💸" label="Expenses" value={fmt(totalExpenses)} color={C.yellow} />
        <KpiCard icon="📊" label="Net (Cash)" value={fmt(netProfit)} color={netProfit >= 0 ? C.green : C.red} />
        <KpiCard icon="🧾" label="GST Collected" value={fmt(gstCollected)} color={C.blue} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Payment mode */}
        <div style={{ background: C.card2, borderRadius: 12, border: `1px solid ${C.border}`, padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 14 }}>Sales by Payment Mode</div>
          {Object.entries(modeBreakdown).length === 0 ? (
            <div style={{ color: C.muted, fontSize: 13 }}>No data</div>
          ) : Object.entries(modeBreakdown).map(([mode, amt]) => (
            <div key={mode} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center' }}>
              <Badge label={mode} />
              <span style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{fmt(amt)}</span>
            </div>
          ))}
        </div>

        {/* Expense breakdown */}
        <div style={{ background: C.card2, borderRadius: 12, border: `1px solid ${C.border}`, padding: 18 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 14 }}>Expenses by Category</div>
          {Object.entries(expBreakdown).length === 0 ? (
            <div style={{ color: C.muted, fontSize: 13 }}>No expenses</div>
          ) : Object.entries(expBreakdown).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
            <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: C.muted2 }}>{cat}</span>
              <span style={{ fontWeight: 700, fontSize: 14, color: C.red }}>{fmt(amt)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Orders Tab (QR / shop-link orders) ────────────────────────────────────────
function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60)   return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

function buildOrderHtml(order, business) {
  const rows = (order.items || []).map((item, i) => {
    const bg = i % 2 === 0 ? '#fff' : '#fafafa';
    return `<tr>
      <td style="background:${bg};padding:10px 14px;font-size:14px;color:#1e293b;font-weight:500;border-bottom:1px solid #f1f5f9">${item.name}</td>
      <td style="background:${bg};padding:10px 14px;font-size:13px;color:#475569;text-align:center;border-bottom:1px solid #f1f5f9;width:50px">×${item.quantity}</td>
      <td style="background:${bg};padding:10px 14px;font-size:13px;color:#475569;text-align:right;border-bottom:1px solid #f1f5f9;width:80px">&#8377;${parseFloat(item.price).toFixed(2)}</td>
      <td style="background:${bg};padding:10px 14px;font-size:14px;color:#1e293b;font-weight:600;text-align:right;border-bottom:1px solid #f1f5f9;width:90px">&#8377;${(item.price * item.quantity).toFixed(2)}</td>
    </tr>`;
  }).join('');
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Order #${order.id}</title></head>
<body style="margin:0;padding:0;background:#f1f5f9;font-family:Arial,sans-serif">
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f1f5f9;padding:32px 0">
<tr><td align="center"><table width="580" cellpadding="0" cellspacing="0" border="0" style="max-width:580px;width:100%;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,.1)">
  <tr><td style="background:linear-gradient(135deg,#FF6B35,#f7522b);padding:24px 28px">
    <div style="font-size:22px;font-weight:800;color:#fff">${business?.name || 'Shop'}</div>
    <div style="font-size:12px;color:rgba(255,255,255,.8);margin-top:6px">${business?.address || ''}</div>
  </td></tr>
  <tr><td style="background:#fff7f4;border-bottom:2px solid #ffe4d6;padding:16px 28px">
    <table width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
      <td><div style="font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:1px">Order</div>
        <div style="font-size:22px;font-weight:800;color:#FF6B35">#${order.id}</div>
        <div style="font-size:12px;color:#64748b">${new Date(order.created_at).toLocaleString('en-IN')}</div>
      </td>
      <td align="right">
        <div style="font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:1px">Customer</div>
        <div style="font-size:16px;font-weight:700;color:#1e293b">${order.customer_name || 'Guest'}</div>
        ${order.customer_email ? `<div style="font-size:12px;color:#64748b">&#9993; ${order.customer_email}</div>` : ''}
        ${order.table_no ? `<div style="font-size:12px;color:#64748b">&#128205; ${order.table_no}</div>` : ''}
      </td>
    </tr></table>
  </td></tr>
  <tr><td style="padding:0 28px"><table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-top:18px">
    <thead><tr style="background:#1e293b">
      <th style="padding:10px 14px;text-align:left;font-size:11px;font-weight:700;color:#94a3b8;letter-spacing:1px">ITEM</th>
      <th style="padding:10px 14px;text-align:center;font-size:11px;font-weight:700;color:#94a3b8;width:50px">QTY</th>
      <th style="padding:10px 14px;text-align:right;font-size:11px;font-weight:700;color:#94a3b8;width:80px">RATE</th>
      <th style="padding:10px 14px;text-align:right;font-size:11px;font-weight:700;color:#94a3b8;width:90px">AMOUNT</th>
    </tr></thead>
    <tbody>${rows}
      <tr><td colspan="3" style="padding:14px;text-align:right;font-size:14px;font-weight:700;color:#1e293b;background:#fff7f4;border-top:2px solid #ffe4d6">TOTAL</td>
        <td style="padding:14px;text-align:right;font-size:20px;font-weight:800;color:#FF6B35;background:#fff7f4;border-top:2px solid #ffe4d6">&#8377;${parseFloat(order.total_amount).toFixed(2)}</td></tr>
    </tbody>
  </table></td></tr>
  ${order.notes ? `<tr><td style="padding:12px 28px"><div style="background:#fefce8;border:1px solid #fde047;border-radius:8px;padding:10px 14px;font-size:13px;color:#713f12"><strong>Note:</strong> ${order.notes}</div></td></tr>` : ''}
  <tr><td style="padding:20px 28px;text-align:center;border-top:1px solid #e2e8f0">
    <div style="font-size:13px;font-weight:700;color:#1e293b">Thank you for your order!</div>
    <div style="font-size:11px;color:#cbd5e1;margin-top:8px">Powered by <span style="font-weight:700;color:#FF6B35">Scanningo.in</span></div>
  </td></tr>
</table></td></tr></table></body></html>`;
}

const STATUS_FLOW = {
  PENDING:         { label: 'Accept Order',         next: 'ACCEPTED',        color: '#FF6B35' },
  ACCEPTED:        { label: 'Mark as Ready',         next: 'SERVED',          color: '#0ea5e9' },
  SERVED:          { label: 'Request Payment',       next: 'PAYMENT_PENDING', color: '#f59e0b' },
  PAYMENT_PENDING: { label: 'Mark as Paid ✓',       next: 'COMPLETED',       color: '#10b981' },
};

function OrderCard({ order, onAction, business }) {
  const [updating, setUpdating] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail]         = useState(order.customer_email || '');
  const [sending, setSending]     = useState(false);
  const [sent, setSent]           = useState(false);
  const [sendErr, setSendErr]     = useState('');

  const flow = STATUS_FLOW[order.status];
  const statusColors = { PENDING: C.yellow, ACCEPTED: C.blue, SERVED: '#0ea5e9', PAYMENT_PENDING: C.yellow, COMPLETED: C.green };
  const sColor = statusColors[order.status] || C.muted;

  const doAction = async () => {
    if (!flow) return;
    setUpdating(true);
    await onAction(order.id, flow.next, order);
    setUpdating(false);
  };

  const doPrint = () => {
    const html = buildOrderHtml(order, business);
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.onload = () => win.print();
  };

  const doSend = async () => {
    if (!email.trim()) { setSendErr('Enter email.'); return; }
    setSending(true); setSendErr('');
    const html = buildOrderHtml(order, business);
    try {
      await sendBillEmail(email.trim(), `Order #${order.id} from ${business?.name || ''}`, html, business?.email || '');
      setSent(true);
    } catch (e) { setSendErr(e.message || 'Could not send.'); }
    setSending(false);
  };

  return (
    <div style={{ background: C.card2, border: `1px solid ${order.status === 'PENDING' ? C.accent + '55' : C.border}`, borderRadius: 12, padding: '16px 18px', marginBottom: 12,
      boxShadow: order.status === 'PENDING' ? `0 0 0 2px ${C.accent}33` : 'none' }}>
      {/* Top row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: C.accent }}>#{order.id}</span>
            <span style={{ background: sColor + '22', color: sColor, border: `1px solid ${sColor}55`, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>{order.status}</span>
            {order.order_type && order.order_type !== 'dine-in' && (
              <span style={{ background: C.yellow + '22', color: C.yellow, border: `1px solid ${C.yellow}55`, borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>{order.order_type}</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{timeAgo(order.created_at)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>₹{order.total_amount}</div>
        </div>
      </div>

      {/* Customer info */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10, fontSize: 12, color: C.muted2 }}>
        <span>👤 {order.customer_name || 'Guest'}</span>
        {order.customer_email && <span style={{ color: C.blue }}>✉ {order.customer_email}</span>}
        {order.table_no && <span>📍 {order.table_no}</span>}
      </div>

      {/* Items */}
      <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
        {(order.items || []).map((item, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.text, padding: '3px 0', borderBottom: i < order.items.length - 1 ? `1px solid ${C.border}` : 'none' }}>
            <span>{item.name} <span style={{ color: C.muted }}>×{item.quantity}</span></span>
            <span style={{ fontWeight: 600 }}>₹{(item.price * item.quantity).toFixed(0)}</span>
          </div>
        ))}
        {order.notes && (
          <div style={{ marginTop: 8, fontSize: 12, color: C.yellow, background: C.yellow + '11', borderRadius: 6, padding: '4px 8px' }}>📝 {order.notes}</div>
        )}
      </div>

      {/* Email form */}
      {showEmail && (
        <div style={{ background: C.card, borderRadius: 8, padding: '12px 14px', marginBottom: 12, border: `1px solid ${C.border}` }}>
          {sent ? (
            <div style={{ textAlign: 'center', fontSize: 13, color: C.green }}>✅ Bill sent to {email}
              <Btn small onClick={() => { setSent(false); setShowEmail(false); }} style={{ marginLeft: 10 }}>Done</Btn>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="email" value={email} onChange={e => { setEmail(e.target.value); setSendErr(''); }}
                placeholder="customer@email.com" autoFocus
                style={{ flex: 1, minWidth: 160, padding: '8px 10px', background: 'rgba(255,255,255,.05)', border: `1px solid ${sendErr ? C.red : C.border}`, borderRadius: 7, fontSize: 13, color: C.text, outline: 'none' }}
                onKeyDown={e => e.key === 'Enter' && doSend()} />
              <Btn small onClick={doSend} disabled={sending}>{sending ? 'Sending…' : 'Send'}</Btn>
              <Btn small outline color={C.muted} onClick={() => { setShowEmail(false); setSendErr(''); }}>Cancel</Btn>
            </div>
          )}
          {sendErr && <div style={{ fontSize: 12, color: C.red, marginTop: 4 }}>{sendErr}</div>}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {flow && order.status !== 'COMPLETED' && (
          <Btn onClick={doAction} disabled={updating} color={flow.color}>
            {updating ? 'Updating…' : flow.label}
          </Btn>
        )}
        {order.status !== 'COMPLETED' && (
          <Btn small outline color={C.muted2} onClick={() => onAction(order.id, 'COMPLETED')}>Skip → Done</Btn>
        )}
        <Btn small color={C.blue} outline onClick={doPrint}>🖨 Print</Btn>
        <Btn small color='#6366f1' outline onClick={() => setShowEmail(v => !v)}>✉ Email</Btn>
      </div>
    </div>
  );
}

// ── Promo email HTML builder ─────────────────────────────────────────────────
function buildPromoHtml(type, customer, itemsList, business, customMsg) {
  const shopName = business?.name || 'Our Store';
  const bid = business?.businessId || '';
  const shopUrl = `https://wonderful-ground-092706600.7.azurestaticapps.net/shop/${bid}`;
  const greeting = `Hi ${customer?.name || 'Valued Customer'},`;
  const headline = type === 'monthly' ? 'Your Monthly Grocery Reminder 🛒' : 'Fresh Stock Just Arrived! 🎉';
  const defaultMsg = type === 'monthly'
    ? "It's time to restock! Based on your purchase history, here are some items you may need:"
    : "Great news! We just received fresh stock of the following items. Come visit us or order online!";
  const bodyText = customMsg || defaultMsg;
  const itemRows = itemsList.map(it => `
    <tr>
      <td style="padding:10px 16px;font-size:14px;color:#1e293b;border-bottom:1px solid #f1f5f9">${it.name}</td>
      <td style="padding:10px 16px;font-size:14px;color:#64748b;text-align:right;border-bottom:1px solid #f1f5f9">&#8377;${parseFloat(it.price || 0).toFixed(2)}</td>
    </tr>`).join('');
  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>${headline}</title></head>
<body style="margin:0;padding:0;background:#f1f5f9;font-family:Arial,sans-serif">
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f1f5f9;padding:32px 0">
<tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,.1)">
  <tr><td style="background:linear-gradient(135deg,#FF6B35,#f7522b);padding:28px 32px">
    <div style="font-size:22px;font-weight:800;color:#fff">${shopName}</div>
    <div style="font-size:14px;color:rgba(255,255,255,0.85);margin-top:6px">${headline}</div>
  </td></tr>
  <tr><td style="padding:28px 32px">
    <p style="font-size:16px;color:#1e293b;margin:0 0 8px;font-weight:600">${greeting}</p>
    <p style="font-size:14px;color:#475569;margin:0 0 24px">${bodyText}</p>
    ${itemRows ? `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin-bottom:24px">
      <tr style="background:#f8fafc">
        <th style="padding:10px 16px;font-size:12px;font-weight:700;color:#64748b;text-align:left;text-transform:uppercase;letter-spacing:.5px">Item</th>
        <th style="padding:10px 16px;font-size:12px;font-weight:700;color:#64748b;text-align:right;text-transform:uppercase;letter-spacing:.5px">Price</th>
      </tr>${itemRows}</table>` : ''}
    <a href="${shopUrl}" style="display:inline-block;background:linear-gradient(135deg,#FF6B35,#f7522b);color:#fff;text-decoration:none;padding:12px 28px;border-radius:8px;font-weight:700;font-size:14px">Order Now &#8594;</a>
  </td></tr>
  <tr><td style="background:#f8fafc;padding:16px 32px;text-align:center">
    <p style="font-size:12px;color:#94a3b8;margin:0">${shopName} &middot; Powered by Scanningo</p>
  </td></tr>
</table></td></tr></table></body></html>`;
}

// ── Customer Lookup Panel ─────────────────────────────────────────────────────
function CustomerLookupPanel({ parties, invoices, items, business, onNewInvoice }) {
  const [query, setQuery]           = useState('');
  const [selected, setSelected]     = useState(null);
  const [showDrop, setShowDrop]     = useState(false);
  const [showNewInv, setShowNewInv] = useState(false);
  const [promoType, setPromoType]   = useState(null);
  const [promoEmail, setPromoEmail] = useState('');
  const [promoMsg, setPromoMsg]     = useState('');
  const [promoItems, setPromoItems] = useState([]);
  const [promoSending, setPromoSending] = useState(false);
  const [promoSent, setPromoSent]   = useState(false);
  const [promoErr, setPromoErr]     = useState('');

  const allCustomers = useMemo(() => {
    const map = {};
    parties.filter(p => p.type === 'customer').forEach(p => {
      const key = p.phone || p.email || p.name;
      map[key] = { ...p, source: 'party' };
    });
    invoices.forEach(inv => {
      if (!inv.party_name || inv.party_name === 'Walk-in') return;
      const key = inv.party_phone || inv.party_name;
      if (!map[key]) {
        map[key] = { name: inv.party_name, phone: inv.party_phone || '', email: inv.party_email || '', address: '', source: 'invoice' };
      } else if (!map[key].email && inv.party_email) {
        map[key].email = inv.party_email;
      }
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
  }, [parties, invoices]);

  const filtered = useMemo(() => {
    if (!query.trim()) return allCustomers;
    const q = query.toLowerCase();
    return allCustomers.filter(c =>
      c.name.toLowerCase().includes(q) ||
      (c.phone || '').includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  }, [query, allCustomers]);

  const customerInvoices = useMemo(() => {
    if (!selected) return [];
    return invoices.filter(inv => {
      if (selected.id && inv.party_id === selected.id) return true;
      if (selected.phone && inv.party_phone === selected.phone) return true;
      return inv.party_name === selected.name;
    }).sort((a, b) => (b.invoice_date || '').localeCompare(a.invoice_date || ''));
  }, [selected, invoices]);

  const totalSpent    = customerInvoices.reduce((s, inv) => s + parseFloat(inv.amount_paid || 0), 0);
  const outstanding   = customerInvoices.reduce((s, inv) => s + parseFloat(inv.balance_due || 0), 0);
  const lastDate      = customerInvoices[0]?.invoice_date;

  const topItems = useMemo(() => {
    const freq = {};
    customerInvoices.forEach(inv => {
      (inv.items || []).forEach(it => {
        if (!freq[it.name]) {
          const mi = items.find(m => m.name === it.name);
          freq[it.name] = { name: it.name, count: 0, price: mi?.price || it.price || 0 };
        }
        freq[it.name].count += parseFloat(it.qty || 1);
      });
    });
    return Object.values(freq).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [customerInvoices, items]);

  const openPromo = (type) => {
    setPromoType(type);
    setPromoEmail(selected?.email || '');
    setPromoMsg('');
    setPromoItems(type === 'monthly' ? [...topItems] : []);
    setPromoSent(false);
    setPromoErr('');
  };

  const sendPromo = async () => {
    const toEmail = promoEmail.trim() || selected?.email;
    if (!toEmail) { setPromoErr('Enter an email address.'); return; }
    setPromoSending(true); setPromoErr('');
    const subj = promoType === 'monthly'
      ? `${business?.name || 'Store'} — Time for your monthly grocery restock!`
      : `${business?.name || 'Store'} — Fresh stock just arrived! 🎉`;
    const html = buildPromoHtml(promoType, selected, promoItems, business, promoMsg);
    try {
      await sendBillEmail(toEmail, subj, html, business?.email || '');
      setPromoSent(true);
    } catch (e) { setPromoErr(e.message || 'Could not send.'); }
    setPromoSending(false);
  };

  const selectCustomer = (c) => { setSelected(c); setQuery(c.name); setShowDrop(false); };

  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 4 }}>Customer Lookup</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>Search by name, phone or email · view history · create invoices · send promotions</div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 24, maxWidth: 500 }}>
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setShowDrop(true); if (selected && e.target.value !== selected.name) setSelected(null); }}
          onFocus={() => setShowDrop(true)}
          placeholder="🔍 Search by name, phone or email…"
          style={{ width: '100%', padding: '12px 16px', background: C.card2, border: `1px solid ${selected ? C.green : C.border}`, borderRadius: 10, fontSize: 14, color: C.text, outline: 'none', boxSizing: 'border-box' }}
        />
        {showDrop && !selected && filtered.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 4, zIndex: 100, maxHeight: 250, overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,.5)' }}>
            {filtered.slice(0, 12).map((c, i) => (
              <div key={i} onClick={() => selectCustomer(c)}
                style={{ padding: '11px 16px', cursor: 'pointer', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{[c.phone, c.email].filter(Boolean).join(' · ')}</div>
                </div>
                {c.source === 'party' && <span style={{ fontSize: 10, background: C.blue + '22', color: C.blue, padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>SAVED</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Customer profile */}
      {selected ? (
        <div>
          {/* Header */}
          <div style={{ background: C.card2, borderRadius: 12, border: `1px solid ${C.border}`, padding: '20px 24px', marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 14 }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 8 }}>{selected.name}</div>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  {selected.phone && <span style={{ fontSize: 13, color: C.muted2 }}>📞 {selected.phone}</span>}
                  {selected.email && <span style={{ fontSize: 13, color: C.blue }}>✉ {selected.email}</span>}
                  {selected.address && <span style={{ fontSize: 13, color: C.muted }}>📍 {selected.address}</span>}
                  {!selected.phone && !selected.email && <span style={{ fontSize: 12, color: C.muted, fontStyle: 'italic' }}>No contact info saved</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Btn onClick={() => setShowNewInv(true)} style={{ background: `linear-gradient(135deg,${C.accent},#f7522b)` }}>+ New Invoice</Btn>
                <Btn outline color={C.blue} onClick={() => openPromo('monthly')}>📅 Monthly Reminder</Btn>
                <Btn outline color={C.green} onClick={() => openPromo('stock')}>🎉 Stock Alert</Btn>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 14 }}>
            {[
              { label: 'Total Spent',    val: fmt(totalSpent),   color: C.green },
              { label: 'Total Invoices', val: customerInvoices.length, color: C.blue },
              { label: 'Outstanding',    val: fmt(outstanding),  color: outstanding > 0 ? C.red : C.muted },
              { label: 'Last Purchase',  val: lastDate || '—',   color: C.muted2, small: true },
            ].map(s => (
              <div key={s.label} style={{ background: C.card2, borderRadius: 10, border: `1px solid ${C.border}`, padding: '14px 16px' }}>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>{s.label}</div>
                <div style={{ fontSize: s.small ? 13 : 20, fontWeight: 800, color: s.color }}>{s.val}</div>
              </div>
            ))}
          </div>

          {/* Frequently bought */}
          {topItems.length > 0 && (
            <div style={{ background: C.card2, borderRadius: 10, border: `1px solid ${C.border}`, padding: '14px 18px', marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Frequently Bought</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {topItems.map((it, i) => (
                  <span key={i} style={{ background: `${C.accent}18`, border: `1px solid ${C.accent}44`, borderRadius: 20, padding: '4px 12px', fontSize: 12, color: C.text }}>
                    {it.name} <span style={{ color: C.muted }}>×{it.count.toFixed(0)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Invoice history */}
          <div style={{ background: C.card2, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
            <div style={{ padding: '12px 18px', borderBottom: `1px solid ${C.border}`, fontSize: 13, fontWeight: 700, color: C.text }}>
              Invoice History ({customerInvoices.length})
            </div>
            {customerInvoices.length === 0 ? (
              <div style={{ padding: '32px 24px', textAlign: 'center', color: C.muted }}>No invoices found for this customer.</div>
            ) : customerInvoices.map(inv => (
              <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderBottom: `1px solid ${C.border}` }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{inv.invoice_no}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{inv.invoice_date} · {(inv.items || []).length} item{(inv.items?.length || 0) !== 1 ? 's' : ''}</div>
                  {inv.notes && <div style={{ fontSize: 11, color: C.muted, marginTop: 2, fontStyle: 'italic' }}>{inv.notes}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>{fmt(inv.total)}</div>
                  <Badge label={inv.payment_status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ background: C.card2, borderRadius: 12, border: `1px solid ${C.border}`, padding: '48px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>👥</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 6 }}>Search a customer</div>
          <div style={{ fontSize: 13, color: C.muted, maxWidth: 360, margin: '0 auto' }}>Enter a name, phone number, or email above to pull up their full history, create invoices, and send promotions.</div>
          {allCustomers.length > 0 && (
            <div style={{ marginTop: 20, display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
              {allCustomers.slice(0, 6).map((c, i) => (
                <span key={i} onClick={() => selectCustomer(c)} style={{ background: `rgba(255,255,255,.06)`, border: `1px solid ${C.border}`, borderRadius: 20, padding: '5px 14px', fontSize: 12, color: C.muted2, cursor: 'pointer' }}>
                  {c.name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* New Invoice modal */}
      {showNewInv && (
        <CreateInvoiceModal parties={parties} items={items} business={business}
          initialParty={selected}
          onClose={() => setShowNewInv(false)}
          onSave={(inv) => { onNewInvoice(inv); setShowNewInv(false); }} />
      )}

      {/* Promo email modal */}
      {promoType && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setPromoType(null); }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: '100%', maxWidth: 560, padding: '24px 26px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{promoType === 'monthly' ? '📅 Monthly Grocery Reminder' : '🎉 New Stock Alert'}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Sending to {selected?.name}</div>
              </div>
              <button onClick={() => setPromoType(null)} style={{ background: 'rgba(255,255,255,.06)', border: 'none', color: C.muted2, borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: 16 }}>✕</button>
            </div>

            <Input label="Recipient Email" value={promoEmail} onChange={e => setPromoEmail(e.target.value)} placeholder="customer@email.com" />
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: C.muted2, fontWeight: 600, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.5px' }}>Custom Message (optional)</label>
              <textarea value={promoMsg} onChange={e => setPromoMsg(e.target.value)}
                placeholder={promoType === 'monthly' ? "It's time to restock your pantry! Here are your usual items…" : "We just got fresh stock! Come visit us or order online!"}
                style={{ width: '100%', padding: '10px 13px', background: 'rgba(255,255,255,.05)', border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, color: C.text, outline: 'none', boxSizing: 'border-box', minHeight: 70, resize: 'vertical' }} />
            </div>

            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, color: C.muted2, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
                {promoType === 'monthly' ? 'Items to remind (from purchase history)' : 'New stock items to highlight'}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 130, overflowY: 'auto' }}>
                {(promoType === 'monthly' ? topItems : items).slice(0, 24).map((it, i) => {
                  const sel = promoItems.some(p => p.name === it.name);
                  return (
                    <span key={i} onClick={() => setPromoItems(prev => sel ? prev.filter(p => p.name !== it.name) : [...prev, it])}
                      style={{ background: sel ? `${C.accent}33` : 'rgba(255,255,255,.05)', border: `1px solid ${sel ? C.accent : C.border}`, borderRadius: 20, padding: '4px 12px', fontSize: 12, color: sel ? C.accent : C.muted2, cursor: 'pointer', userSelect: 'none' }}>
                      {it.name}
                    </span>
                  );
                })}
              </div>
              {promoItems.length === 0 && <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Tap items above to include them in the email.</div>}
            </div>

            {promoSent ? (
              <div style={{ background: `${C.green}22`, border: `1px solid ${C.green}55`, borderRadius: 8, padding: '12px 16px', fontSize: 14, color: C.green, textAlign: 'center', fontWeight: 600 }}>✓ Email sent successfully to {promoEmail}!</div>
            ) : (
              <>
                {promoErr && <div style={{ background: `${C.red}22`, border: `1px solid ${C.red}55`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: C.red, marginBottom: 12 }}>{promoErr}</div>}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <Btn outline color={C.muted} onClick={() => setPromoType(null)}>Cancel</Btn>
                  <Btn onClick={sendPromo} disabled={promoSending}>{promoSending ? 'Sending…' : 'Send Email'}</Btn>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function OrdersTab({ orders, onAction, business, parties, invoices, items, onNewInvoice }) {
  const activeOrders = orders.filter(o => o.status !== 'COMPLETED');
  const done         = orders.filter(o => o.status === 'COMPLETED');
  const [showDone, setShowDone] = useState(false);
  const [view, setView]         = useState('orders');

  return (
    <div>
      {/* Toggle header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 2, background: 'rgba(255,255,255,.05)', borderRadius: 11, padding: 4 }}>
          <button onClick={() => setView('orders')} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', background: view === 'orders' ? C.accent : 'transparent', color: view === 'orders' ? '#fff' : C.muted, transition: 'all .15s' }}>
            🛎️ QR Orders {activeOrders.length > 0 ? `(${activeOrders.length})` : ''}
          </button>
          <button onClick={() => setView('customers')} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', background: view === 'customers' ? C.accent : 'transparent', color: view === 'customers' ? '#fff' : C.muted, transition: 'all .15s' }}>
            👥 Customers
          </button>
        </div>
        {view === 'orders' && activeOrders.length > 0 && (
          <span style={{ background: C.red + '22', color: C.red, border: `1px solid ${C.red}55`, borderRadius: 20, padding: '4px 14px', fontSize: 13, fontWeight: 700 }}>{activeOrders.length} active</span>
        )}
      </div>

      {/* QR Orders view */}
      {view === 'orders' && (
        <>
          {activeOrders.length === 0 && (
            <div style={{ background: C.card2, borderRadius: 12, border: `1px solid ${C.border}`, padding: '40px 24px', textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>🛎️</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 6 }}>No active orders</div>
              <div style={{ fontSize: 13, color: C.muted }}>Orders placed via your QR code or shop link will appear here in real time.</div>
            </div>
          )}
          {activeOrders.map(o => <OrderCard key={o.id} order={o} onAction={onAction} business={business} />)}
          {done.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <button onClick={() => setShowDone(v => !v)} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 13, cursor: 'pointer', padding: '4px 0', fontWeight: 600 }}>
                {showDone ? '▾' : '▸'} Completed Orders ({done.length})
              </button>
              {showDone && done.map(o => <OrderCard key={o.id} order={o} onAction={onAction} business={business} />)}
            </div>
          )}
        </>
      )}

      {/* Customer Lookup view */}
      {view === 'customers' && (
        <CustomerLookupPanel parties={parties} invoices={invoices} items={items} business={business} onNewInvoice={onNewInvoice} />
      )}
    </div>
  );
}

// ── Invoice HTML builder (for print & email) ──────────────────────────────────
function buildInvoiceHtml(inv, business) {
  const rows = (inv.items || []).map((item, i) => {
    const bg = i % 2 === 0 ? '#ffffff' : '#fafafa';
    const disc = item.discount > 0 ? `<br/><small style="color:#94a3b8">Disc ${item.discount}%</small>` : '';
    const tax  = item.tax_rate > 0 ? `<br/><small style="color:#94a3b8">GST ${item.tax_rate}%</small>` : '';
    return `<tr>
      <td style="background:${bg};padding:10px 14px;font-size:13px;color:#64748b;border-bottom:1px solid #f1f5f9;width:28px">${i + 1}</td>
      <td style="background:${bg};padding:10px 14px;font-size:14px;color:#1e293b;font-weight:500;border-bottom:1px solid #f1f5f9">${item.name}${disc}${tax}</td>
      <td style="background:${bg};padding:10px 14px;font-size:13px;color:#475569;text-align:center;border-bottom:1px solid #f1f5f9;width:50px">${item.qty} ${item.unit || ''}</td>
      <td style="background:${bg};padding:10px 14px;font-size:13px;color:#475569;text-align:right;border-bottom:1px solid #f1f5f9;width:80px">&#8377;${parseFloat(item.price).toFixed(2)}</td>
      <td style="background:${bg};padding:10px 14px;font-size:14px;color:#1e293b;font-weight:600;text-align:right;border-bottom:1px solid #f1f5f9;width:90px">&#8377;${parseFloat(item.amount).toFixed(2)}</td>
    </tr>`;
  }).join('');
  const statusColor = inv.payment_status === 'paid' ? '#10b981' : inv.payment_status === 'partial' ? '#f59e0b' : '#ef4444';
  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>${inv.invoice_no}</title></head>
<body style="margin:0;padding:0;background:#f1f5f9;font-family:Arial,sans-serif">
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#f1f5f9;padding:32px 0">
<tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,.1)">
  <tr><td style="background:linear-gradient(135deg,#FF6B35,#f7522b);padding:28px 32px">
    <table width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
      <td><div style="font-size:24px;font-weight:800;color:#fff">${business?.name || ''}</div>
        <div style="font-size:12px;color:rgba(255,255,255,.85);margin-top:10px;line-height:1.8">
          ${business?.address ? `&#128205; ${business.address}<br>` : ''}
          ${business?.phone   ? `&#128222; ${business.phone}` : ''}
        </div>
      </td>
      <td align="right" valign="top">
        <div style="background:rgba(255,255,255,.2);border:2px solid rgba(255,255,255,.5);border-radius:6px;padding:5px 12px;display:inline-block;color:#fff;font-size:11px;font-weight:700;letter-spacing:2px">TAX INVOICE</div>
        <div style="margin-top:8px;background:${statusColor}22;border:1px solid ${statusColor};border-radius:12px;padding:4px 12px;color:${statusColor};font-size:11px;font-weight:700;text-transform:uppercase">${inv.payment_status}</div>
      </td>
    </tr></table>
  </td></tr>
  <tr><td style="background:#fff7f4;border-bottom:2px solid #ffe4d6;padding:20px 32px">
    <table width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
      <td>
        <div style="font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">Invoice No.</div>
        <div style="font-size:22px;font-weight:800;color:#FF6B35">${inv.invoice_no}</div>
        <div style="font-size:12px;color:#64748b;margin-top:4px">Date: ${inv.invoice_date}</div>
      </td>
      <td align="right">
        <div style="font-size:11px;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">Bill To</div>
        <div style="font-size:17px;font-weight:700;color:#1e293b">${inv.party_name || 'Walk-in'}</div>
        ${inv.party_phone ? `<div style="font-size:12px;color:#64748b;margin-top:3px">&#128222; ${inv.party_phone}</div>` : ''}
      </td>
    </tr></table>
  </td></tr>
  <tr><td style="padding:0 32px">
    <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-top:20px">
      <thead><tr style="background:#1e293b">
        <th style="padding:10px 14px;text-align:left;font-size:11px;font-weight:700;color:#94a3b8;letter-spacing:1px;width:28px">#</th>
        <th style="padding:10px 14px;text-align:left;font-size:11px;font-weight:700;color:#94a3b8;letter-spacing:1px">ITEM</th>
        <th style="padding:10px 14px;text-align:center;font-size:11px;font-weight:700;color:#94a3b8;letter-spacing:1px;width:50px">QTY</th>
        <th style="padding:10px 14px;text-align:right;font-size:11px;font-weight:700;color:#94a3b8;letter-spacing:1px;width:80px">RATE</th>
        <th style="padding:10px 14px;text-align:right;font-size:11px;font-weight:700;color:#94a3b8;letter-spacing:1px;width:90px">AMOUNT</th>
      </tr></thead>
      <tbody>${rows}
        ${inv.discount_amount > 0 ? `<tr><td colspan="4" style="padding:8px 14px;text-align:right;font-size:13px;color:#64748b;background:#f8fafc">Discount</td><td style="padding:8px 14px;text-align:right;font-size:13px;color:#ef4444;background:#f8fafc">- &#8377;${parseFloat(inv.discount_amount).toFixed(2)}</td></tr>` : ''}
        ${inv.tax_amount > 0 ? `<tr><td colspan="4" style="padding:8px 14px;text-align:right;font-size:13px;color:#64748b;background:#f8fafc">Tax (GST)</td><td style="padding:8px 14px;text-align:right;font-size:13px;color:#475569;background:#f8fafc">&#8377;${parseFloat(inv.tax_amount).toFixed(2)}</td></tr>` : ''}
        <tr><td colspan="4" style="padding:14px 14px;text-align:right;font-size:14px;font-weight:700;color:#1e293b;background:#fff7f4;border-top:2px solid #ffe4d6">TOTAL AMOUNT</td>
          <td style="padding:14px 14px;text-align:right;font-size:20px;font-weight:800;color:#FF6B35;background:#fff7f4;border-top:2px solid #ffe4d6">&#8377;${parseFloat(inv.total).toFixed(2)}</td></tr>
        ${inv.balance_due > 0 ? `<tr><td colspan="4" style="padding:8px 14px;text-align:right;font-size:13px;color:#64748b;background:#fef2f2">Balance Due</td><td style="padding:8px 14px;text-align:right;font-size:14px;font-weight:700;color:#ef4444;background:#fef2f2">&#8377;${parseFloat(inv.balance_due).toFixed(2)}</td></tr>` : ''}
      </tbody>
    </table>
  </td></tr>
  ${inv.notes ? `<tr><td style="padding:12px 32px"><div style="background:#fefce8;border:1px solid #fde047;border-radius:8px;padding:10px 14px;font-size:13px;color:#713f12"><strong>Note:</strong> ${inv.notes}</div></td></tr>` : ''}
  <tr><td style="padding:24px 32px;text-align:center;border-top:1px solid #e2e8f0">
    <div style="font-size:14px;font-weight:700;color:#1e293b;margin-bottom:4px">Thank you for your business!</div>
    <div style="font-size:11px;color:#cbd5e1;margin-top:10px">Powered by <span style="font-weight:700;color:#FF6B35">Scanningo.in</span></div>
  </td></tr>
</table></td></tr></table></body></html>`;
}

// ── Invoice View Modal (items + print + email) ─────────────────────────────────
function InvoiceViewModal({ invoice, business, onClose }) {
  const [sendEmail, setSendEmail] = useState(false);
  const [email, setEmail]         = useState('');
  const [sending, setSending]     = useState(false);
  const [sent, setSent]           = useState(false);
  const [sendErr, setSendErr]     = useState('');

  const html = buildInvoiceHtml(invoice, business);

  const doPrint = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.onload = () => win.print();
  };

  const doSend = async () => {
    if (!email.trim()) { setSendErr('Enter customer email.'); return; }
    setSending(true); setSendErr('');
    try {
      await sendBillEmail(email.trim(), `Invoice ${invoice.invoice_no} from ${business?.name || ''}`, html, business?.email || '');
      setSent(true);
    } catch (e) {
      setSendErr(e.message || 'Could not send email.');
    }
    setSending(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px', overflowY: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: '100%', maxWidth: 680, boxShadow: '0 24px 64px rgba(0,0,0,.6)' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: `1px solid ${C.border}` }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: C.text }}>{invoice.invoice_no}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{invoice.party_name || 'Walk-in'} · {invoice.invoice_date}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Badge label={invoice.payment_status} />
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,.06)', border: 'none', color: C.muted2, borderRadius: 7, padding: '5px 12px', cursor: 'pointer', fontSize: 16 }}>✕</button>
          </div>
        </div>

        <div style={{ padding: '20px 22px' }}>
          {/* Items table */}
          <div style={{ background: C.card2, borderRadius: 10, border: `1px solid ${C.border}`, overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 90px 90px', background: '#111827', padding: '9px 14px', gap: 4 }}>
              {['Item', 'Qty', 'Rate', 'Amount'].map(h => (
                <div key={h} style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase' }}>{h}</div>
              ))}
            </div>
            {(invoice.items || []).map((item, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 90px 90px', padding: '10px 14px', borderBottom: `1px solid ${C.border}`, gap: 4, alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{item.name}</div>
                  {item.tax_rate > 0 && <div style={{ fontSize: 11, color: C.muted }}>GST {item.tax_rate}%</div>}
                </div>
                <div style={{ fontSize: 13, color: C.muted2 }}>{item.qty} {item.unit}</div>
                <div style={{ fontSize: 13, color: C.muted2 }}>{fmt(item.price)}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{fmt(item.amount)}</div>
              </div>
            ))}
            {/* Totals */}
            <div style={{ padding: '12px 14px', borderTop: `1px solid ${C.border}` }}>
              {invoice.discount_amount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted, marginBottom: 4 }}><span>Discount</span><span style={{ color: C.red }}>- {fmt(invoice.discount_amount)}</span></div>}
              {invoice.tax_amount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted, marginBottom: 4 }}><span>Tax (GST)</span><span>{fmt(invoice.tax_amount)}</span></div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 800, color: C.accent, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                <span>Total</span><span>{fmt(invoice.total)}</span>
              </div>
              {invoice.balance_due > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.red, marginTop: 4 }}>
                  <span>Balance Due</span><span style={{ fontWeight: 700 }}>{fmt(invoice.balance_due)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div style={{ background: 'rgba(245,158,11,0.1)', border: `1px solid ${C.yellow}44`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: C.yellow, marginBottom: 16 }}>
              📝 {invoice.notes}
            </div>
          )}

          {/* Email send panel */}
          {sendEmail ? (
            <div style={{ background: C.card2, borderRadius: 10, border: `1px solid ${C.border}`, padding: '16px' }}>
              {sent ? (
                <div style={{ textAlign: 'center', padding: '12px 0' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>🎉</div>
                  <div style={{ fontWeight: 700, color: C.green, fontSize: 15, marginBottom: 4 }}>Bill Sent!</div>
                  <div style={{ fontSize: 12, color: C.muted }}>Delivered to {email}</div>
                  <Btn small onClick={() => { setSent(false); setSendEmail(false); }} style={{ marginTop: 12 }}>Done</Btn>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 10 }}>Send invoice by email</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="email" value={email} onChange={e => { setEmail(e.target.value); setSendErr(''); }}
                      placeholder="customer@email.com" autoFocus
                      style={{ flex: 1, padding: '9px 12px', background: 'rgba(255,255,255,.05)', border: `1px solid ${sendErr ? C.red : C.border}`, borderRadius: 8, fontSize: 13, color: C.text, outline: 'none' }}
                      onKeyDown={e => e.key === 'Enter' && doSend()} />
                    <Btn onClick={doSend} disabled={sending} color={C.accent}>{sending ? 'Sending…' : 'Send'}</Btn>
                    <Btn outline color={C.muted} onClick={() => { setSendEmail(false); setSendErr(''); }}>Cancel</Btn>
                  </div>
                  {sendErr && <div style={{ fontSize: 12, color: C.red, marginTop: 6 }}>{sendErr}</div>}
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Btn onClick={doPrint} color={C.blue} style={{ flex: 1 }}>🖨 Print Invoice</Btn>
              <Btn onClick={() => setSendEmail(true)} color={C.accent} style={{ flex: 1 }}>📧 Email Bill</Btn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── QR & Share Tab ────────────────────────────────────────────────────────────
function QRTab({ business }) {
  const shopUrl = `${window.location.origin}/shop/${business?.businessId}`;
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(shopUrl).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  const printQR = () => {
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>QR Code</title>
      <style>body{font-family:Arial,sans-serif;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;margin:0;background:#fff}
      h2{font-size:22px;margin-bottom:4px}p{color:#555;font-size:14px;margin:4px 0 20px}small{color:#999;font-size:11px;margin-top:16px}</style>
    </head><body>
      <h2>${business?.name || 'Our Store'}</h2>
      <p>Scan to browse menu & place order</p>
      <div id="qr"></div>
      <small>${shopUrl}</small>
      <script src="https://unpkg.com/qrcode/build/qrcode.min.js"></script>
      <script>QRCode.toCanvas(document.getElementById('qr'),'${shopUrl}',{width:280},()=>{});</script>
    </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 1200);
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>QR Code & Share</div>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>Share this link with customers so they can scan & order online</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 32, alignItems: 'start' }}>
        {/* QR */}
        <div style={{ background: '#fff', padding: 24, borderRadius: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, border: `3px solid ${C.accent}`, boxShadow: `0 0 0 6px ${C.accent}22` }}>
          <QRCode value={shopUrl} size={200} fgColor="#0f1117" bgColor="#ffffff" />
          <div style={{ fontSize: 11, color: '#555', textAlign: 'center', maxWidth: 200, wordBreak: 'break-all' }}>{shopUrl}</div>
        </div>

        {/* Info & actions */}
        <div>
          <div style={{ background: C.card2, borderRadius: 12, border: `1px solid ${C.border}`, padding: '18px 20px', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.muted2, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.5px' }}>Your Shop Link</div>
            <div style={{ fontSize: 14, color: C.accent, wordBreak: 'break-all', fontWeight: 600, marginBottom: 14 }}>{shopUrl}</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Btn onClick={copy} color={copied ? C.green : C.blue}>{copied ? '✓ Copied!' : 'Copy Link'}</Btn>
              <Btn onClick={printQR} color={C.accent}>🖨 Print QR</Btn>
            </div>
          </div>

          <div style={{ background: C.card2, borderRadius: 12, border: `1px solid ${C.border}`, padding: '18px 20px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>How to use</div>
            {[
              ['Share with customers', 'Send the link via WhatsApp, SMS, or print the QR code and display it at your counter.'],
              ['Customers scan & order', 'They see your full menu and can place orders directly from their phone.'],
              ['You get notified', 'New orders appear on your Dashboard instantly.'],
            ].map(([title, desc]) => (
              <div key={title} style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <span style={{ fontSize: 18, marginTop: 1 }}>✅</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{title}</div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Settings Tab ──────────────────────────────────────────────────────────────
function SettingsTab({ business, refreshBusiness }) {
  const [form, setForm] = useState({
    name: business?.name || '',
    phone: business?.phone || '',
    email: business?.email || '',
    address: business?.address || '',
    description: business?.description || '',
    gst_percent: business?.gst_percent || 0,
    business_type: business?.business_type || 'retail',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await updateMe(form);
      await refreshBusiness();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 20 }}>Business Settings</div>
      <div style={{ background: C.card2, borderRadius: 14, border: `1px solid ${C.border}`, padding: 24 }}>
        <Input label="Business Name" value={form.name} onChange={e => set('name', e.target.value)} required />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label="Phone" value={form.phone} onChange={e => set('phone', e.target.value)} />
          <Input label="Email" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
        </div>
        <Input label="Address" value={form.address} onChange={e => set('address', e.target.value)} />
        <Input label="Business Description" value={form.description} onChange={e => set('description', e.target.value)} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label="Default GST %" type="number" value={form.gst_percent} onChange={e => set('gst_percent', e.target.value)} />
          <div>
            <label style={{ display: 'block', fontSize: 11, color: C.muted2, fontWeight: 600, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.5px' }}>Business Type</label>
            <select value={form.business_type} onChange={e => set('business_type', e.target.value)}
              style={{ width: '100%', padding: '10px 13px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, color: C.text, outline: 'none' }}>
              <option value="retail">Retail Store</option>
              <option value="grocery">Grocery / Kirana</option>
              <option value="wholesale">Wholesale / Distributor</option>
              <option value="pharmacy">Pharmacy</option>
              <option value="electronics">Electronics</option>
              <option value="restaurant">Restaurant / Cafe (switch to food mode)</option>
            </select>
          </div>
        </div>
        <Btn onClick={save} disabled={saving} style={{ marginTop: 8, background: saved ? C.green : `linear-gradient(135deg,${C.accent},#f7522b)` }}>
          {saving ? 'Saving…' : saved ? '✓ Saved!' : 'Save Settings'}
        </Btn>
      </div>
    </div>
  );
}

// ── How It Works — Guide Tab ──────────────────────────────────────────────────
const GUIDE_PHASES = [
  {
    id: 'setup', label: 'Setup', icon: '⚙️', color: '#6366f1',
    tagline: 'One-time configuration before you start',
    steps: [
      { icon: '👥', screen: 'Parties', title: 'Add Suppliers', desc: 'Go to Parties → Add Party → Type = Supplier. Enter the supplier name, phone number, email and address. These are the people/companies you buy stock from.' },
      { icon: '📦', screen: 'Items', title: 'Add Your Products', desc: 'Go to Items → Add Item. Enter the product name, sale price (what you charge customers), purchase price (what you pay supplier), unit (kg / L / pcs / box), and GST %.' },
      { icon: '🗄️', screen: 'Inventory', title: 'Set Opening Stock', desc: 'Go to Inventory. For each product, set the current quantity you have in stock and the minimum stock level. When stock falls below minimum, you see a red alert badge.' },
    ]
  },
  {
    id: 'purchase', label: 'Restocking', icon: '🛒', color: '#f59e0b',
    tagline: 'Buy stock from your suppliers',
    steps: [
      { icon: '👥', screen: 'Parties', title: 'Add a Customer', desc: 'Go to Parties → Add Party → Type = Customer. Save name, phone, email. This lets you look up their history, send bills by email, and send promotions later.' },
      { icon: '🛒', screen: 'Purchases', title: 'Record a Purchase', desc: 'Go to Purchases → Add Purchase. Select your supplier, add each item you bought with quantity and price paid. Choose payment mode (Cash / Bank Transfer / Credit). This tracks your cost of goods.' },
      { icon: '📈', screen: 'Inventory', title: 'Update Stock Count', desc: 'After a purchase, go to Inventory and increase the stock quantity for the items you received. The Items tab will now show green stock badges instead of red.' },
    ]
  },
  {
    id: 'sell', label: 'Direct Sale', icon: '📄', color: '#10b981',
    tagline: 'Bill walk-in customers at the counter',
    steps: [
      { icon: '📄', screen: 'Sales Invoice', title: 'Create Sales Invoice', desc: 'Go to Sales Invoice → New Invoice. Select the customer (or tick "Walk-in" for unknown customers). Add items from your list — price and GST fill automatically.' },
      { icon: '🏷️', screen: 'Sales Invoice', title: 'Apply Discounts', desc: 'On any item row, type a discount % (e.g. 10%) and the amount deducts live. At the bottom, enter a flat ₹ discount off the full bill. The grand total recalculates instantly.' },
      { icon: '🖨️', screen: 'Sales Invoice', title: 'Print or Send Bill', desc: 'Click View on the invoice → Print for a paper receipt, or type the customer email to send a branded digital bill. The bill shows your shop name, items, tax, and total.' },
    ]
  },
  {
    id: 'qr', label: 'QR Orders', icon: '📱', color: '#FF6B35',
    tagline: 'Let customers order from their phones',
    steps: [
      { icon: '🔗', screen: 'QR & Share', title: 'Share Your Shop Link', desc: 'Go to QR & Share. Print the QR code and place it on your counter / door. Or copy the shop link and share it on WhatsApp, Instagram, or anywhere. Customers scan to browse your full menu.' },
      { icon: '🛎️', screen: 'Orders', title: 'Order Arrives in Real Time', desc: 'When a customer places an order, a notification sound plays and the Orders tab shows a red badge with the count. You see their name, address, items ordered, and total — instantly.' },
      { icon: '✅', screen: 'Orders', title: 'Accept → Pack → Mark Paid', desc: 'Click Accept Order → items go to packing. Click Mark as Ready → customer is notified. Click Request Payment → Mark as Paid ✓ once payment is collected. Each step is one tap.' },
      { icon: '🧾', screen: 'Sales Invoice', title: 'Invoice Auto-Created', desc: 'The moment you mark the order as Paid, a Sales Invoice is automatically created with the customer name, items, address, and total. It appears in Sales Invoice and on the Dashboard.' },
    ]
  },
  {
    id: 'crm', label: 'Customer CRM', icon: '💼', color: '#0ea5e9',
    tagline: 'Retain customers with smart follow-ups',
    steps: [
      { icon: '🔍', screen: 'Orders → Customers', title: 'Look Up Any Customer', desc: 'Go to Orders → tap the Customers tab. Type any name, phone number, or email. Their profile shows total spent, number of invoices, outstanding balance, and every past purchase.' },
      { icon: '📅', screen: 'Orders → Customers', title: 'Send Monthly Reminder', desc: 'On the customer profile → Monthly Reminder. Their most-frequently-bought items are pre-selected. Add a personal message. Send a professional branded email reminding them to restock.' },
      { icon: '🎉', screen: 'Orders → Customers', title: 'Announce New Stock', desc: 'On the customer profile → Stock Alert. Pick the new items you just received. Hit Send — the customer gets a branded email: "Fresh stock just arrived! Order now." with a direct order link.' },
    ]
  },
  {
    id: 'reports', label: 'Track & Grow', icon: '📊', color: '#8b5cf6',
    tagline: 'Understand your business performance',
    steps: [
      { icon: '💰', screen: 'Dashboard', title: 'Daily Business Snapshot', desc: 'The Dashboard shows today\'s total sales, total outstanding (unpaid invoices), pending QR orders, and recent invoice list — your entire business at a glance the moment you open the app.' },
      { icon: '💸', screen: 'Expenses', title: 'Track Expenses', desc: 'Go to Expenses → Add Expense. Log rent, electricity, staff salary, supplies etc. with amount, date and category. This helps you see true profit vs revenue.' },
      { icon: '📈', screen: 'Reports', title: 'Sales & Profit Reports', desc: 'Go to Reports for sales trend charts, expense breakdown by category, profit margin, and party-wise outstanding. See which items sell the most and who owes you money.' },
    ]
  },
];

function GuideTab() {
  const isMobile = useIsMobile();
  const [phaseIdx, setPhaseIdx]   = useState(0);
  const [stepIdx,  setStepIdx]    = useState(0);
  const [playing,  setPlaying]    = useState(true);
  const [animKey,  setAnimKey]    = useState(0);

  const phase = GUIDE_PHASES[phaseIdx];
  const totalSteps = GUIDE_PHASES.reduce((s, p) => s + p.steps.length, 0);
  const globalStep = GUIDE_PHASES.slice(0, phaseIdx).reduce((s, p) => s + p.steps.length, 0) + stepIdx;

  const advance = useCallback(() => {
    const cur = GUIDE_PHASES[phaseIdx];
    if (stepIdx < cur.steps.length - 1) {
      setStepIdx(s => s + 1);
    } else if (phaseIdx < GUIDE_PHASES.length - 1) {
      setPhaseIdx(p => p + 1); setStepIdx(0);
    } else {
      setPhaseIdx(0); setStepIdx(0);
    }
    setAnimKey(k => k + 1);
  }, [phaseIdx, stepIdx]);

  const back = useCallback(() => {
    if (stepIdx > 0) { setStepIdx(s => s - 1); }
    else if (phaseIdx > 0) { setPhaseIdx(p => p - 1); setStepIdx(GUIDE_PHASES[phaseIdx - 1].steps.length - 1); }
    setAnimKey(k => k + 1);
  }, [phaseIdx, stepIdx]);

  useEffect(() => {
    if (!playing) return;
    const id = setTimeout(advance, 3200);
    return () => clearTimeout(id);
  }, [playing, advance]);

  const goPhase = (i) => { setPhaseIdx(i); setStepIdx(0); setPlaying(false); setAnimKey(k => k + 1); };

  return (
    <div style={{ maxWidth: 800 }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: isMobile ? 17 : 18, fontWeight: 800, color: C.text }}>🎓 How It Works</div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Complete workflow guide — auto-plays like a tutorial · tap any step to explore</div>
      </div>

      {/* Phase overview bar */}
      <div style={{ background: C.card2, borderRadius: 14, border: `1px solid ${C.border}`, padding: '16px 18px', marginBottom: 18, overflowX: 'auto' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Full System Flow</div>
        <div style={{ display: 'flex', alignItems: 'center', minWidth: isMobile ? 540 : 0 }}>
          {GUIDE_PHASES.map((p, i) => (
            <React.Fragment key={p.id}>
              <div onClick={() => goPhase(i)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, padding: '8px 10px', borderRadius: 10, background: i === phaseIdx ? `${p.color}22` : 'transparent', border: `1.5px solid ${i === phaseIdx ? p.color : i < phaseIdx ? p.color + '44' : C.border}`, cursor: 'pointer', minWidth: isMobile ? 70 : 90, transition: 'all .2s', boxShadow: i === phaseIdx ? `0 0 0 3px ${p.color}22` : 'none' }}>
                <span style={{ fontSize: isMobile ? 18 : 22 }}>{p.icon}</span>
                <span style={{ fontSize: isMobile ? 9 : 10, fontWeight: 700, color: i === phaseIdx ? p.color : i < phaseIdx ? C.muted2 : C.muted, textAlign: 'center', lineHeight: 1.2 }}>{p.label}</span>
                {i < phaseIdx && <span style={{ fontSize: 8, color: C.green, fontWeight: 800 }}>✓ done</span>}
              </div>
              {i < GUIDE_PHASES.length - 1 && (
                <div style={{ flex: 1, height: 2, minWidth: 8, background: i < phaseIdx ? `linear-gradient(90deg,${p.color},${GUIDE_PHASES[i+1].color})` : 'rgba(255,255,255,.07)', borderRadius: 1, margin: '0 2px', position: 'relative', marginBottom: 14 }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Current phase detail */}
      <div style={{ background: C.card2, borderRadius: 16, border: `2px solid ${phase.color}44`, padding: isMobile ? '16px 14px' : '22px', marginBottom: 14, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 180, height: 180, background: `radial-gradient(circle,${phase.color}18 0%,transparent 70%)`, pointerEvents: 'none' }} />

        {/* Phase header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ width: 46, height: 46, borderRadius: 13, background: `${phase.color}22`, border: `1.5px solid ${phase.color}66`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>{phase.icon}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: phase.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Phase {phaseIdx + 1} of {GUIDE_PHASES.length}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{phase.label}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{phase.tagline}</div>
          </div>
          {playing && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.muted, flexShrink: 0 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: phase.color, animation: 'guidePulse 1.2s ease-in-out infinite' }} />
              Auto-playing
            </div>
          )}
        </div>

        {/* Step list */}
        {phase.steps.map((s, i) => {
          const done = i < stepIdx;
          const current = i === stepIdx;
          const future = i > stepIdx;
          return (
            <div key={`${phaseIdx}-${i}-${animKey}`}>
              <div onClick={() => { setStepIdx(i); setPlaying(false); setAnimKey(k => k + 1); }}
                style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 12, cursor: 'pointer', marginBottom: 0,
                  background: current ? `${phase.color}18` : done ? 'rgba(16,185,129,.06)' : 'transparent',
                  border: `1px solid ${current ? phase.color + '55' : done ? C.green + '33' : 'transparent'}`,
                  opacity: future ? 0.4 : 1, transition: 'opacity .3s, background .3s, border .3s',
                  animation: current ? 'guideStepIn .45s ease-out' : 'none',
                }}>
                {/* Bubble */}
                <div style={{ flexShrink: 0, width: 38, height: 38, borderRadius: '50%', background: done ? C.green : current ? phase.color : 'rgba(255,255,255,.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: done ? 14 : 20, color: '#fff', fontWeight: 800, transition: 'all .3s', marginTop: 1, boxShadow: current ? `0 0 0 5px ${phase.color}28` : 'none' }}>
                  {done ? '✓' : s.icon}
                </div>
                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: current ? 6 : 2 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: current ? C.text : done ? C.muted2 : C.muted }}>{s.title}</div>
                    <span style={{ background: `${phase.color}22`, color: phase.color, border: `1px solid ${phase.color}44`, borderRadius: 7, padding: '2px 8px', fontSize: 10, fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>→ {s.screen}</span>
                  </div>
                  {(current || done) && (
                    <div style={{ fontSize: isMobile ? 11 : 12, color: C.muted2, lineHeight: 1.65 }}>{s.desc}</div>
                  )}
                  {current && playing && (
                    <div style={{ height: 2, background: 'rgba(255,255,255,.06)', borderRadius: 1, marginTop: 10, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: phase.color, borderRadius: 1, animation: 'guideTimer 3.2s linear forwards' }} />
                    </div>
                  )}
                </div>
              </div>
              {i < phase.steps.length - 1 && (
                <div style={{ paddingLeft: 30, height: 12, display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: 2, height: 12, background: done ? C.green + '66' : C.border, borderRadius: 1, transition: 'background .4s' }} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Progress */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.muted, marginBottom: 5 }}>
          <span>Step {globalStep + 1} of {totalSteps}</span>
          <span>{Math.round((globalStep / (totalSteps - 1)) * 100)}% of full guide</span>
        </div>
        <div style={{ height: 5, background: 'rgba(255,255,255,.05)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${(globalStep / (totalSteps - 1)) * 100}%`, background: `linear-gradient(90deg,${C.accent},${phase.color})`, borderRadius: 3, transition: 'width .6s ease' }} />
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn small outline color={C.muted} onClick={back}>← Back</Btn>
          <Btn small color={phase.color} onClick={advance}>Next →</Btn>
        </div>
        <button onClick={() => setPlaying(v => !v)}
          style={{ background: playing ? `${C.accent}18` : 'rgba(255,255,255,.06)', border: `1px solid ${playing ? C.accent : C.border}`, borderRadius: 8, color: playing ? C.accent : C.muted2, padding: '7px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          {playing ? '⏸ Pause' : '▶ Auto-Play'}
        </button>
      </div>

      <style>{`
        @keyframes guideStepIn { from { opacity:0; transform:translateX(-10px); } to { opacity:1; transform:translateX(0); } }
        @keyframes guideTimer  { from { width:0% } to { width:100% } }
        @keyframes guidePulse  { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.3)} }
      `}</style>
    </div>
  );
}

// ── Main RetailDashboard ──────────────────────────────────────────────────────
const POLL_MS = 5000;

export default function RetailDashboard() {
  const { business, logout, refreshBusiness } = useAuth();
  const [active,      setActive]      = useState('dashboard');
  const [invoices,    setInvoices]    = useState([]);
  const [parties,     setParties]     = useState([]);
  const [items,       setItems]       = useState([]);
  const [purchases,   setPurchases]   = useState([]);
  const [expenses,    setExpenses]    = useState([]);
  const [orders,      setOrders]      = useState([]);
  const [inventory,   setInventory]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [viewInvoice, setViewInvoice] = useState(null);
  const prevOrderIds  = useRef(null);

  // ── Mobile / Fullscreen ───────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [isFS, setIsFS]         = useState(false);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || !!window.navigator.standalone;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onFSChange = () => setIsFS(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener('fullscreenchange', onFSChange);
    document.addEventListener('webkitfullscreenchange', onFSChange);
    return () => { document.removeEventListener('fullscreenchange', onFSChange); document.removeEventListener('webkitfullscreenchange', onFSChange); };
  }, []);

  const toggleFS = () => {
    if (isFS) {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    } else {
      const el = document.documentElement;
      (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
    }
  };

  // Initial load of all data
  useEffect(() => {
    Promise.all([getInvoices(), getParties(), getMenu(), getPurchases(), getExpenses(), getInventory()])
      .then(([inv, par, itm, pur, exp, invt]) => {
        setInvoices(inv); setParties(par); setItems(itm); setPurchases(pur); setExpenses(exp); setInventory(invt);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Poll QR orders every 5 s
  const fetchOrders = useCallback(async () => {
    try {
      const latest = await getOrders();
      if (prevOrderIds.current !== null) {
        const newPending = latest.filter(
          o => o.status === 'PENDING' && !prevOrderIds.current.has(o.id)
        );
        if (newPending.length > 0) {
          try { new Audio('data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAA...').play(); } catch {}
        }
      }
      prevOrderIds.current = new Set(latest.map(o => o.id));
      setOrders(latest);
    } catch { /* silent — noLogout already set in api.js */ }
  }, []);

  useEffect(() => {
    fetchOrders();
    const id = setInterval(fetchOrders, POLL_MS);
    return () => clearInterval(id);
  }, [fetchOrders]);

  const handleOrderAction = useCallback(async (id, status, orderData) => {
    await updateOrderStatus(id, status);
    setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));

    if (status === 'COMPLETED' && orderData) {
      try {
        const orderItems = (orderData.items || []).map(it => {
          const qty = parseFloat(it.quantity || it.qty || 1);
          const price = parseFloat(it.price || 0);
          const taxRate = parseFloat(it.tax_rate || 0);
          return {
            name: it.name,
            qty,
            unit: it.unit || 'pcs',
            price,
            discount: 0,
            tax_rate: taxRate,
            amount: +(price * qty * (1 + taxRate / 100)).toFixed(2),
          };
        });
        const subtotal = orderItems.reduce((s, r) => s + r.price * r.qty, 0);
        const taxTotal = orderItems.reduce((s, r) => s + r.price * r.qty * r.tax_rate / 100, 0);
        const total = parseFloat(orderData.total_amount || subtotal + taxTotal);
        const addressNote = orderData.table_no ? `Delivery: ${orderData.table_no}` : (orderData.address ? `Delivery: ${orderData.address}` : '');
        const notes = `QR Order #${orderData.id}${addressNote ? ' | ' + addressNote : ''}`;

        const inv = await addInvoice({
          invoice_date: new Date().toISOString().split('T')[0],
          party_name: orderData.customer_name || 'Walk-in',
          party_phone: orderData.customer_phone || '',
          items: orderItems,
          subtotal: +subtotal.toFixed(2),
          discount_amount: 0,
          tax_amount: +taxTotal.toFixed(2),
          total: +total.toFixed(2),
          payment_mode: 'cash',
          amount_paid: +total.toFixed(2),
          notes,
        });
        setInvoices(prev => [inv, ...prev]);
      } catch (e) {
        console.warn('Auto-invoice from order failed:', e);
      }
    }
  }, [setInvoices]);

  const handleNewInvoiceFromCustomer = useCallback((inv) => setInvoices(prev => [inv, ...prev]), [setInvoices]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.bg, color: C.muted, fontSize: 16 }}>
      Loading…
    </div>
  );

  const pendingOrders = orders.filter(o => o.status === 'PENDING').length;
  const handleViewInvoice = (inv) => setViewInvoice(inv);

  const contentPanes = (
    <>
      {active === 'dashboard'  && <DashboardTab invoices={invoices} parties={parties} items={items} expenses={expenses} setActive={setActive} onViewInvoice={handleViewInvoice} pendingOrders={pendingOrders} />}
      {active === 'orders'     && <OrdersTab orders={orders} onAction={handleOrderAction} business={business} parties={parties} invoices={invoices} items={items} onNewInvoice={handleNewInvoiceFromCustomer} />}
      {active === 'sales'      && <SalesTab invoices={invoices} setInvoices={setInvoices} parties={parties} items={items} business={business} onViewInvoice={handleViewInvoice} />}
      {active === 'parties'    && <PartiesTab parties={parties} setParties={setParties} />}
      {active === 'items'      && <ItemsTab items={items} setItems={setItems} inventory={inventory} />}
      {active === 'inventory'  && <InventoryTab />}
      {active === 'purchases'  && <PurchasesTab purchases={purchases} setPurchases={setPurchases} parties={parties} items={items} />}
      {active === 'expenses'   && <ExpensesTab expenses={expenses} setExpenses={setExpenses} />}
      {active === 'reports'    && <ReportsTab invoices={invoices} expenses={expenses} parties={parties} />}
      {active === 'qr'         && <QRTab business={business} />}
      {active === 'settings'   && <SettingsTab business={business} refreshBusiness={refreshBusiness} />}
      {active === 'guide'      && <GuideTab />}
    </>
  );

  const font = "Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";

  if (isMobile) {
    return (
      <MobileCtx.Provider value={true}>
        <div style={{ height: '100dvh', background: C.bg, fontFamily: font, position: 'relative', overflow: 'hidden' }}>
          <MobileTopBar business={business} pendingOrders={pendingOrders} isFullscreen={isFS} onToggleFS={toggleFS} isStandalone={isStandalone} />
          <div style={{ position: 'absolute', top: 56, bottom: 64, left: 0, right: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px 14px' }}>
            {contentPanes}
          </div>
          <MobileBottomNav active={active} setActive={setActive} pendingOrders={pendingOrders} logout={logout} />
          {viewInvoice && <InvoiceViewModal invoice={viewInvoice} business={business} onClose={() => setViewInvoice(null)} />}
        </div>
      </MobileCtx.Provider>
    );
  }

  return (
    <MobileCtx.Provider value={false}>
      <div style={{ display: 'flex', height: '100vh', background: C.bg, fontFamily: font }}>
        <Sidebar active={active} setActive={setActive} business={business} logout={logout} pendingOrders={pendingOrders} />
        <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>
          {contentPanes}
        </div>
        {viewInvoice && <InvoiceViewModal invoice={viewInvoice} business={business} onClose={() => setViewInvoice(null)} />}
      </div>
    </MobileCtx.Provider>
  );
}
